import { lazy, Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import LocalGame from './components/game/LocalGame';
import AvvisoRientro from './components/shared/AvvisoRientro';
import AvvisoAltroDispositivo from './components/shared/AvvisoAltroDispositivo';
import AvvisoSenzaRete from './components/shared/AvvisoSenzaRete';
import AvvisoDiServizio from './components/shared/AvvisoDiServizio';
import BoardThemeApplier from './components/game/BoardThemeApplier';
import PuzzlePage from './components/puzzle/PuzzlePage';
import PuzzleUnavailable from './components/puzzle/PuzzleUnavailable';
import DailyPuzzleInvitePage from './components/puzzle/DailyPuzzleInvitePage';
import OnlineGame from './components/online/OnlineGame';
import AuthModal from './components/auth/AuthModal';
import GuestBanner from './components/auth/GuestBanner';
import OverlaySoloIscritti from './components/auth/OverlaySoloIscritti';
import TournamentLobby from './components/tournament/TournamentLobby';
import TournamentGame from './components/tournament/TournamentGame';
import ArenaGame from './components/arena/ArenaGame';
import TermsPage from './components/legal/TermsPage';
import PrivacyPage from './components/legal/PrivacyPage';
import RulesPage from './components/rules/RulesPage';
import RulesOpeningsPage from './components/rules/RulesOpeningsPage';
import RulesCapturePage from './components/rules/RulesCapturePage';
import RulesIndexPage from './components/rules/RulesIndexPage';
import RulesGamePage from './components/rules/RulesGamePage';
import RulesTournamentsPage from './components/rules/RulesTournamentsPage';
import { ancoraCapitolo, type TournamentRulesAnchor } from './rules/tournamentRules';
import {
  buildRulesPath,
  buildRulesIndexPath,
  buildGameRulesPath,
  buildTournamentRulesPath,
  buildOpeningsPath,
  buildCapturePath,
  buildContentPath,
  parseContentPath,
  isRulesLang,
  type RulesLang,
} from './seo/routes';
import { usePageHead, type HeadTarget } from './seo/usePageHead';
import VerifyEmailPage from './components/auth/VerifyEmailPage';
import ResetPasswordPage from './components/auth/ResetPasswordPage';
import Layout from './components/layout/Layout';
import LegalFooter from './components/common/LegalFooter';
import { useAppUpdate } from './hooks/useAppUpdate';
import { useLaunchHandler } from './hooks/useLaunchHandler';
import { isSafeToReload } from './utils/safe-to-reload';
import { isPuzzleVariant } from './utils/puzzle-variants';
/*
 * I lettori del link di una gara stanno con il COSTRUTTORE, in `link-gara.ts`:
 * `percorsoGara` scrive `/?tournament=<id>` e `parseTournamentDeepLink` lo
 * rilegge, e quelle due forme devono combaciare al carattere. Finche' stavano in
 * due file diversi l'unica cosa che le teneva d'accordo era un commento per
 * parte.
 */
import { parseTournamentDeepLink, parseArenaDeepLink, percorsoGara } from './utils/link-gara';

// Lazy-loaded route components (code splitting)
const ProfilePage = lazy(() => import('./components/profile/ProfilePage'));
/* Lazy anche questa: era l'unico import statico rimasto verso GameReplay, e da
   sola teneva il replay (col suo CSS) nel preload d'ingresso dell'app. */
const GamesHistoryPage = lazy(() => import('./components/games-history/GamesHistoryPage'));
const LeaderboardPage = lazy(() => import('./components/leaderboard/LeaderboardPage'));
/* Non pigro: e' la porta della barra, e un'attesa fra il tocco e il foglio si
   sente. Pesa poco — riusa card, guscio e token che ci sono gia'. */
const ArenaFinalResultsPage = lazy(() => import('./components/arena/ArenaFinalResultsPage'));
/* Non lazy: è il fallback che copre il download del chunk della lista. */
import TournamentListSkeleton from './components/tournament/TournamentListSkeleton';
const ArenaDetailPage = lazy(() => import('./components/arena/ArenaDetailPage'));
const TournamentListPage = lazy(() => import('./components/tournament/TournamentListPage'));
const TournamentDetailPage = lazy(() => import('./components/tournament/TournamentDetailPage'));
const FaqPage = lazy(() => import('./components/faq/FaqPage'));
const DashboardPage = lazy(() => import('./components/dashboard/DashboardPage'));
const SpectateGame = lazy(() => import('./components/online/SpectateGame'));
// Board editor: shipped to production. Only the ANALYSIS overlay inside it is still
// dev + staging gated, and that gate lives in EditorScreen.tsx next to the thing it hides.
const EditorScreen = lazy(() => import('./components/editor/EditorScreen'));
// I due attrezzi del menu «Strumenti», accanto all'editor perche' si aprono dalla
// stessa voce di barra. Import NON condizionali: queste due schermate vanno in
// produzione, al contrario dell'anteprima dei problemi qui sotto.
const OrologioScreen = lazy(() => import('./components/strumenti/OrologioScreen'));
const EstraiAperturaScreen = lazy(() => import('./components/strumenti/EstraiAperturaScreen'));
// "Study this position" hand-off from the game review to the editor: the position has to
// be loaded BEFORE the screen change, so EditorScreen mounts with it already there.
const loadEditorPosition = (board: BoardState) =>
  import('./store/editor-store').then((m) => m.useEditorStore.getState().loadPosition(board));
// Same hand-off for a SHARED position (?editor=<fen>&variant=…). `loadSavedPosition` and
// not `importFen`: the variant travels with the FEN, and the same FEN read with the wrong
// geometry is a different position — or none, in which case this resolves to false.
const loadEditorFen = (fen: string, variant: GameVariant) =>
  import('./store/editor-store').then((m) => m.useEditorStore.getState().loadSavedPosition(fen, variant));
// DEV-ONLY daily-puzzle preview. The DEV-conditional ternary is the key to
// EXCLUDING it from production: Vite statically replaces `import.meta.env.DEV`
// with `false` in prod, so the `import()` sits in a dead branch and Rollup never
// emits the chunk (verified by grepping the prod bundle). A plain unconditional
// `lazy(() => import(...))` WOULD ship it.
const PuzzlePreviewPage = import.meta.env.DEV
  ? lazy(() => import('./components/puzzle/PuzzlePreviewPage'))
  : null;
// Static import of a tiny cache-only module (not the lazy DashboardPage chunk) so
// logout can drop the cached dashboard summary without bloating the main bundle.
import { clearDashboardCache } from './components/dashboard/dashboard-cache';
import CookieBanner from './components/layout/CookieBanner';
import HomePage from './components/home/HomePage';
import { NavItem } from './components/layout/Sidebar';
import { useGameStore, isHotseatMode, GameMode } from './store/game-store';
import { useMultiplayerStore } from './store/multiplayer-store';
import { laPartitaOnlineEStataRilasciata, laPartitaDiTorneoEStataRilasciata, laPartitaDiTorneoEInUnaSchermataSbagliata, doveTornareDopoIlRilascio, doveAtterraLaVoceDelTorneo, doveRimbalzaLaSchermataDelTorneo } from './utils/rientro-navigazione';
import { useAuthStore } from './store/auth-store';
import { usePreferencesStore } from './store/preferences-store';
import { useTournamentStore } from './store/tournament-store';
import { useArenaStore } from './store/arena-store';
import { GameVariant, TimeControlPreset, GameStatus, Color, VALID_VARIANT_STRINGS, toGameVariant } from '@draughts/shared';
import type { ChallengeSettings, BoardState } from '@draughts/shared';
// Alias: `GameMode` in shared è la modalità di gioco (normal/breakthrough/antidraughts),
// mentre il `GameMode` del game-store è il tipo di partita (pvp/ai-*), già importato qui.
import type { GameMode as RuleMode } from '@draughts/shared';
import type { AIDifficulty, AIGameOptions } from './components/dashboard/DashboardPlayAIModal';
import { useTranslation } from 'react-i18next';
import { useOnlineStore } from './store/online-store';
import { useLobbyStore } from './store/lobby-store';
import {
  cancelChallenge as socketCancelChallenge,
  createLinkChallenge,
  cancelLinkChallenge,
} from './services/socket';
import GlobalIncomingChallengeBanner from './components/online/GlobalIncomingChallengeBanner';
import OpenInAppBanner from './components/shared/OpenInAppBanner';
import QueuedTableBanner from './components/lobby/QueuedTableBanner';
import BandaGaraInCorso from './components/shared/BandaGaraInCorso';
import AvvisoLobby from './components/lobby/AvvisoLobby';
import PlayOverlay from './components/lobby/PlayOverlay';
import ConnectedUsersModal from './components/online/ConnectedUsersModal';
import type { OnlineUser } from './store/online-store';
import CustomTableHost from './components/lobby/CustomTableHost';
import FoglioGioca from './components/layout/FoglioGioca';
import FoglioStrumenti from './components/layout/FoglioStrumenti';
import GlobalIncomingRematchBanner from './components/online/GlobalIncomingRematchBanner';
import OutgoingChallengeModal from './components/online/OutgoingChallengeModal';
import ChallengeLinkModal from './components/online/ChallengeLinkModal';
import ChallengeInvitePage from './components/online/ChallengeInvitePage';
import { fetchChallengeLink } from './services/challenge-api';
import {
  getStoredChallengeLinkId,
  setStoredChallengeLinkId,
  clearStoredChallengeLinkId,
} from './utils/challenge-link-storage';
import {
  getPendingPuzzleLink,
  setPendingPuzzleLink,
  clearPendingPuzzleLink,
} from './utils/pending-puzzle-link';
import {
  salvaInvitoInSospeso,
  leggiInvitoInSospeso,
  dimenticaInvitoInSospeso,
  decidiRitornoAlTorneo,
} from './utils/pending-tournament-invite';
import { trackPageView, trackLogout, trackGameStarted, trackFirstGameEver, trackVariantBreadth, trackSessionStart, trackChallengeLinkCreated, trackGuestPlayClicked } from './utils/analytics';
import { playSound } from './utils/audio';
import { startInPersonGame } from './utils/start-in-person-game';
import { log } from './utils/logger';
import { chiaveErroreOnlinePer } from './utils/chiave-errore-online';
import { useReportPwaInstall } from './hooks/useReportPwaInstall';
import { sendChallenge } from './services/socket';
import { fetchDailyPuzzle } from './services/puzzle-api';
import { useBackNavigation, type AppLocation } from './hooks/useBackNavigation';
import { useLente } from './hooks/useLente';
import { useTorneoDopoLaRiconnessione } from './hooks/useTorneoDopoLaRiconnessione';
import { useArenaQueueGuard } from './hooks/useArenaQueueGuard';
import { useArenaPresence } from './hooks/useArenaPresence';
import { useVisibility } from './hooks/useVisibility';
import { useScadenzaPresenza } from './hooks/useScadenzaPresenza';
import { risvegliaSocket } from './services/socket';
import { useArenaResultsCleanup } from './hooks/useArenaResultsCleanup';
import { useArchiviazioneTorneo } from './hooks/useArchiviazioneTorneo';
import { useIsMobile } from './hooks/useIsMobile';
import { runAfterProgrammaticPops } from './hooks/useOverlayBackButton';
import { useStudyHandoff } from './hooks/useStudyInEditor';
import { laPartitaDellaSchermataEInCorso } from './utils/partita-in-corso';
import { getLastVariant } from './utils/last-used-play';
import { ratingBucketFrom } from './utils/rating-bucket';
import DashboardPuzzleModal from './components/dashboard/DashboardPuzzleModal';
import { DEMO_INT_PUZZLE } from './data/demo-puzzle';
import './App.css';
import './components/online/OnlineGame.css';

type AppScreen =
  | 'landing'
  | 'home'
  | 'local-game'
  | 'online-game'
  | 'spectate'
  | 'profile'
  | 'games-history'
  | 'leaderboard'
  | 'arena-detail'
  | 'arena-results'
  | 'arena-game'
  | 'tournaments'
  | 'tournament-detail'
  | 'tournament-lobby'
  | 'tournament-game'
  | 'puzzle'
  | 'puzzle-invite'
  | 'puzzle-preview'
  | 'terms'
  | 'privacy'
  | 'faq'
  | 'rules-index'
  | 'rules-game'
  | 'rules-variant'
  | 'rules-openings'
  | 'rules-capture'
  | 'rules-tournaments'
  | 'editor'
  | 'orologio'
  | 'estrai-apertura'
  | 'verify-email'
  | 'reset-password'
  | 'challenge-invite';

/**
 * L'URL canonico di una schermata, nella lingua data. Unica definizione: la usano
 * sia `locationToUrl` (che scrive la cronologia) sia l'effetto che tiene il path
 * allineato alla lingua. Le schermate senza un URL proprio — una partita, il
 * profilo, la classifica — stanno sulla landing, che e' dove la SPA vive davvero.
 */
function contentUrlFor(
  lang: RulesLang,
  screen: AppScreen,
  rulesVariant: GameVariant | null,
): string {
  if (rulesVariant) {
    if (screen === 'rules-openings') return buildOpeningsPath(lang, rulesVariant);
    if (screen === 'rules-capture') return buildCapturePath(lang, rulesVariant);
    if (screen === 'rules-variant') return buildRulesPath(lang, rulesVariant);
  }
  if (screen === 'rules-index') return buildRulesIndexPath(lang);
  if (screen === 'rules-game') return buildGameRulesPath(lang);
  if (screen === 'rules-tournaments') return buildTournamentRulesPath(lang);
  if (screen === 'faq' || screen === 'terms' || screen === 'privacy') {
    return buildContentPath(lang, screen);
  }
  return buildContentPath(lang, 'landing');
}

// AI difficulty (picker value) → game-store GameMode. Shared by every vs-AI entry
// point (home/dashboard modal, rules CTA, and the solved-puzzle continuation).
const AI_MODE_MAP: Record<AIDifficulty, GameMode> = {
  'very-easy': 'ai-very-easy',
  easy: 'ai-easy',
  medium: 'ai-medium',
  hard: 'ai-hard',
  expert: 'ai-expert',
  master: 'ai-master',
};
const AI_DIFFICULTY_ORDER: AIDifficulty[] = ['very-easy', 'easy', 'medium', 'hard', 'expert', 'master'];

/**
 * I carichi dei link condivisi (vedi utils/share.ts), letti in un posto solo
 * cosi' che la cattura al primo render e `applyDeepLink` qui sotto non possano
 * dire due cose diverse dello stesso link — lo leggono in due momenti diversi.
 *
 * Qui restano il puzzle e l'editor. Il link di una GARA — torneo o arena — si
 * legge in `utils/link-gara.ts`, accanto alla funzione che lo scrive.
 */
function parsePuzzleDeepLink(params: URLSearchParams): string | null {
  if (params.get('puzzle') !== 'daily') return null;
  const variant = (params.get('variant') || 'international').toLowerCase();
  // Solo le varianti che la pagina dei puzzle mostra: un link non apre una variante
  // tenuta fuori (Frysk! lo e' stata fino ai suoi primi 100 problemi).
  return isPuzzleVariant(variant) ? variant : null;
}

/**
 * Il capitolo del regolamento dei tornei chiesto dal frammento dell'URL.
 *
 * Accetta tre forme, e le ultime due sono la ragione per cui la risoluzione sta
 * in `ancoraCapitolo` e non in una RegExp: `#tiebreaks` e' l'ancora di oggi,
 * `#tornei-spareggi` e `#faq-tornei-spareggi` sono quelle di ieri, di quando
 * queste nove regole erano voci della FAQ. Quelle URL sono gia' state mandate
 * in chat e indicizzate; il frammento non arriva mai al server, quindi nessun
 * redirect di nginx puo' raccoglierle.
 *
 * Si legge QUI, dentro `applyDeepLink`, e non nella pagina: poche righe piu' in
 * basso quella stessa funzione fa `replaceState` sul solo `pathname`, cioe'
 * butta via il frammento prima che qualunque componente possa vederlo.
 */
function ancoraTorneiDaHash(hash: string): TournamentRulesAnchor | null {
  const nudo = hash.replace(/^#(?:faq-)?/, '');
  return nudo ? ancoraCapitolo(nudo) : null;
}

/** Anything malformed is dropped rather than carried around: an unknown variant,
 *  or a FEN longer than the server's own 512-char ceiling. */
function parseEditorDeepLink(params: URLSearchParams): { fen: string; variant: string } | null {
  const fen = params.get('editor');
  if (!fen || fen.length > 512) return null;
  const variant = (params.get('variant') || 'international').toLowerCase();
  if (!VALID_VARIANT_STRINGS.includes(variant)) return null;
  return { fen, variant };
}

/** Whether a URL carries any shared payload at all — what tells OpenInAppBanner
 *  this visitor followed a link from outside rather than opening the site. */
function hasSharedPayload(params: URLSearchParams): boolean {
  return (
    parsePuzzleDeepLink(params) !== null ||
    params.has('editor') ||
    params.has('challenge') ||
    params.has('game') ||
    // L'invito a un torneo arriva da WhatsApp come tutti gli altri: senza
    // questa riga il banner «apri nell'app» non lo riconoscerebbe.
    params.has('tournament') ||
    /* Come sopra: senza questa riga il banner «apri nell'app» non
       riconoscerebbe l'invito a un'arena arrivato da WhatsApp. */
    params.has('arena')
  );
}

function App() {
  const { t, i18n } = useTranslation();

  // ── Shared daily-puzzle link ────────────────────────────────────────────
  // Captured during the FIRST render — before the mount effect below strips the
  // query off the URL. Declared above `screen` because `screen` initialises from
  // it: landing on the gate must not flash the home page first.
  const [dailyDeepLinkFromUrl, setDailyDeepLinkFromUrl] = useState<string | null>(() =>
    typeof window === 'undefined'
      ? null
      : parsePuzzleDeepLink(new URLSearchParams(window.location.search))
  );
  // A link a previous visit stored because the visitor had to authenticate
  // first. NEVER hijacks the screen — it only arms the resolver below, so a
  // fresh registration's email round trip still lands on the puzzle. Were it to
  // drive `screen` too, the /verify?token=… landing would be replaced by the
  // gate before the token was ever submitted.
  const [dailyDeepLinkStored] = useState<string | null>(() => getPendingPuzzleLink()?.variant ?? null);
  const dailyDeepLinkVariant = dailyDeepLinkFromUrl ?? dailyDeepLinkStored;

  // ── Shared position link ────────────────────────────────────────────────
  // /?editor=<fen>&variant=… — the position travels IN the link (a saved position
  // is private to its owner, so there is nothing to fetch). Captured during the
  // first render like the puzzle link above, before the mount effect strips the
  // query. Anything malformed is dropped here rather than carried around: an
  // unknown variant, or a FEN longer than the server's own 512-char ceiling.
  const [editorDeepLink, setEditorDeepLink] = useState<{ fen: string; variant: string } | null>(
    () =>
      typeof window === 'undefined'
        ? null
        : parseEditorDeepLink(new URLSearchParams(window.location.search))
  );
  // Declared up here, not next to its resolver: `applyDeepLink` has to re-arm it
  // when a second launch arrives, and that runs far above the resolver.
  const editorDeepLinkDone = useRef(false);

  // ── Arrival, for OpenInAppBanner ────────────────────────────────────────
  // The URL this visit STARTED on. The mount effect strips the payload off the
  // address bar, so by the time the banner renders `location.href` is a bare `/`
  // — reopening that in the app would land on the home page instead of the
  // shared challenge, which looks exactly like the bug the banner is there to fix.
  const [arrivalUrl] = useState(() =>
    typeof window === 'undefined' ? '' : window.location.href
  );
  const [arrivedFromShare] = useState(() =>
    typeof window === 'undefined'
      ? false
      : hasSharedPayload(new URLSearchParams(window.location.search))
  );

  const [screen, setScreen] = useState<AppScreen>(() =>
    dailyDeepLinkFromUrl ? 'puzzle-invite' : 'home'
  );
  // Last known scroll offset of the home screen, preserved across the unmount/
  // remount that navigating away (and Back) performs. Restored on return so the
  // user lands where they clicked; zeroed at deliberate "go home" sites (menu,
  // logout, leave-game) so those start at the top. See the scroll effects below.
  const homeScrollRef = useRef(0);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalInitialMode, setAuthModalInitialMode] = useState<'login' | 'register'>('login');
  /*
   * Ogni porta d'accesso passa di qui, e riporta il modo a «accedi».
   *
   * `authModalInitialMode` e' appiccicoso: sopravvive alla chiusura del modale.
   * I venticinque `onLoginClick` scrivevano `setShowAuthModal(true)` e basta,
   * quindi dopo un qualunque giro dalla registrazione il bottone «Accedi» della
   * navbar riapriva la registrazione. Un solo posto dove dirlo, invece di
   * venticinque dove dimenticarselo.
   */
  const apriAccesso = () => {
    /*
     * L'accesso non si dirotta MAI, nemmeno per un ospite.
     *
     * Qui c'era una guardia che mandava l'ospite alla CONVERSIONE, per non
     * fargli creare un conto nuovo buttando via le partite che ha giocato. Ma
     * quella è una preoccupazione della porta «Registrati», e su questa porta
     * murava l'unica via di rientro: un utente registrato che gioca da ospite
     * premeva «Accedi» e si trovava «Crea Account», senza modo di uscirne.
     * La guardia resta dove serve — `registerFromPuzzle` e `GuestBanner`.
     */
    setAuthModalInitialMode('login');
    setShowAuthModal(true);
  };
  /*
   * L'altra porta: «Registrati». Questa SI' che si dirotta, e per il motivo
   * opposto a quello per cui l'accesso non si dirotta mai.
   *
   * Un ospite che si registra da zero si fabbrica un conto NUOVO, e quello che
   * aveva resta appeso a un'identita' che non riaprira' mai piu'. Non da'
   * errore: sparisce e basta. La conversione tiene la stessa riga `users`,
   * quindi tutto quello che le punta per chiave sopravvive.
   *
   * «Quello che aveva» sono le sue PARTITE, e non e' un elenco piu' lungo: un
   * ospite non ha rating — le sue colonne tengono il default e non le ha
   * guadagnate (`rating-policy.ts`) — ne' problemi, che gli sono chiusi su ogni
   * rotta (`requireRegistered`), quindi nemmeno un rating dei problemi o dei
   * tentativi. Le partite bastano: su di loro poggia anche la streak di
   * attivita', che e' derivata da quelle e riparte sul conto convertito.
   *
   * La guardia stava in un punto solo (`registerFromPuzzle`); ogni altro punto
   * che apriva la registrazione la ignorava. Ora e' qui, e chi apre quella porta
   * passa da qui — compreso `registerFromPuzzle`, che di suo tiene solo la parte
   * che lo riguarda: smontare il foglio del puzzle, che sta sopra al modale.
   */
  const apriRegistrazione = () => {
    if (useAuthStore.getState().isGuest) {
      setShowUpgradeModal(true);
      return;
    }
    setAuthModalInitialMode('register');
    setShowAuthModal(true);
  };
  const [profileUsername, setProfileUsername] = useState<string | null>(null);
  // Tab the profile screen opens on (null = its own default, 'games'). Only the
  // home hero's gear shortcut sets it; every other route into the profile goes
  // through openProfile() without a tab, which resets it.
  const [profileTab, setProfileTab] = useState<string | null>(null);
  /** Voce di `faq.entries` da aprire all'arrivo sulla FAQ (null = nessuna). */
  const [faqEntry, setFaqEntry] = useState<string | null>(null);
  /** Capitolo da mostrare all'arrivo sulle regole dei tornei (null = dall'inizio). */
  const [tournamentAnchor, setTournamentAnchor] = useState<TournamentRulesAnchor | null>(null);
  // Tab/variant/finestra the leaderboard screen opens on (null = its own
  // defaults, 'allTime'/international/30 giorni). Only the most-active card sets
  // them, so the link lands on the same ranking the user was just looking at;
  // every other route goes through openLeaderboard() with no arguments, which
  // resets. La finestra sta accanto alla variante perche' quella card adesso le
  // sceglie entrambe: portarne una sola voleva dire atterrare su una classifica
  // diversa da quella da cui si e' cliccato.
  const [leaderboardTab, setLeaderboardTab] = useState<string | null>(null);
  const [leaderboardVariant, setLeaderboardVariant] = useState<string | null>(null);
  const [leaderboardPeriod, setLeaderboardPeriod] = useState<number | null | undefined>(undefined);
  const [rulesVariant, setRulesVariant] = useState<GameVariant | null>(null);
  // Pending "play this variant" request triggered from a rules page; consumed by
  // HomePage/DashboardPage on the home screen to auto-open the matching play modal.
  const [pendingPlay, setPendingPlay] = useState<{ variant: GameVariant; mode: 'ai' | 'online' } | null>(null);
  const [selectedTournamentId, setSelectedTournamentId] = useState<string | null>(null);
  const [selectedArenaId, setSelectedArenaId] = useState<string | null>(null);
  const [selectedPuzzleId, setSelectedPuzzleId] = useState<number | null>(null);
  const [puzzlePractice, setPuzzlePractice] = useState(false);
  // Daily-puzzle mobile sheet: on phones "Risolvi" opens a full-screen overlay
  // (like the play modals) instead of switching to the full puzzle screen.
  const [puzzleOverlayId, setPuzzleOverlayId] = useState<number | null>(null);
  const [puzzleOverlayPractice, setPuzzleOverlayPractice] = useState(false);
  // Landing "Risolvi" demo: the fixed demo_int puzzle, validated in-browser with
  // no auth. Tracked separately from the (auth-gated) daily puzzle above.
  const [puzzleDemo, setPuzzleDemo] = useState(false);
  const [puzzleOverlayDemo, setPuzzleOverlayDemo] = useState(false);
  // DEV-ONLY daily-puzzle preview (see the 'puzzle-preview' screen). Reached via
  // /?puzzle=preview[&variant=…][&date=YYYY-MM-DD] on a dev build only.
  const [previewVariant, setPreviewVariant] = useState('italian');
  const [previewDate, setPreviewDate] = useState<string | null>(null);
  const puzzleSheet = useIsMobile(768);
  // Lista "Problemi del giorno" (un problema per variante), aperta dal bottone
  // della hero. Lo stato sta qui e non in DashboardPage perche' su desktop
  // aprire un problema cambia schermata e smonta la dashboard: al ritorno la
  // lista deve essere ancora aperta, sotto al problema appena chiuso.
  const [showDailyPuzzles, setShowDailyPuzzles] = useState(false);
  // Riaprire la lista al rientro sulla home, dopo un problema aperto da li'
  // (solo desktop: vedi openPuzzleFromDailyList).
  const [reopenDailyPuzzles, setReopenDailyPuzzles] = useState(false);

  /*
   * Il foglio di «Gioca»: il menu che sale dal basso dalla voce centrale della
   * barra (FoglioGioca.tsx). Lo stato sta qui, e il foglio si monta una volta
   * sola dentro `challengeOverlays`, che e' gia' sparso su ogni ramo del
   * render: la barra si vede da quasi ovunque, quindi il suo menu deve potersi
   * aprire da quasi ovunque. L'alternativa — passarlo per `Layout` fino a
   * `Sidebar` — vorrebbe dire sei prop nuove su venticinque `<Layout>`.
   */
  const [foglioGiocaAperto, setFoglioGiocaAperto] = useState(false);
  /*
   * Il foglio di «Strumenti»: lo stesso meccanismo, dalla quarta voce della
   * barra (FoglioStrumenti.tsx). Due stati e non uno con dentro il nome del
   * foglio aperto: i due menu non possono stare su insieme — ci pensa
   * `handleNavigate` — ma ognuno ha la sua voce di barra accesa, la sua
   * destinazione messa da parte e il suo effetto di chiusura, e un unico stato
   * li avrebbe intrecciati senza far risparmiare una riga.
   */
  const [strumentiAperto, setStrumentiAperto] = useState(false);
  /*
   * La sfoglia della partita personalizzata aperta dal foglio, gia' puntata
   * sulla destinazione che e' stata toccata. Un solo stato per tre righe —
   * Sfida AI, Sfida diretta, In presenza — perche' CustomTableHost le raggiunge
   * tutte con `defaultOpponent`: tre montaggi diversi sarebbero tre copie dello
   * stesso cablaggio.
   */
  const [avversarioSfoglia, setAvversarioSfoglia] = useState<'computer' | 'challenge' | 'inperson' | null>(null);
  /*
   * Il giocatore scelto nella lista accanto al lobby, che la sfoglia qui sotto
   * riceve gia' puntato (`preselectedUser`). Serve uno stato suo perche'
   * `avversarioSfoglia` dice solo QUALE destinazione aprire, non su chi.
   */
  const [collegatoSfidato, setCollegatoSfidato] = useState<OnlineUser | null>(null);
  /*
   * La coppia «Gioca» + collegati, da 768px in su. Stessa guardia della home, e per
   * la stessa ragione: a cambiare e' la FORMA del velo, non il suo aspetto.
   * Non e' la larghezza vietata da `foglio-gioca.cablaggio.test.ts` — quella
   * decideva SE il foglio delle porte esistesse, questa decide come si impagina un
   * velo che esiste comunque.
   */
  const coppiaAffiancata = !useIsMobile(768);
  /*
   * L'OROLOGIO DAL VIVO CHE CORRE: la barra in basso si toglie di mezzo.
   *
   * Lo dice `OrologioScreen` con `onCorsa`, e vale solo SUL TELEFONO. Non e' la
   * larghezza vietata da `foglio-gioca.cablaggio.test.ts` — quella decideva se
   * un menu esistesse — ed e' qui per la stessa ragione per cui
   * `useAltezzaBarra` dichiara il 768: sotto quella soglia la barra e' una riga
   * in basso e ruba altezza ai due quadranti, sopra e' la colonna di sinistra e
   * non ne ruba nessuna.
   *
   * ⚠️ L'hook si chiama SEMPRE, su una riga sua. Scritto
   * `orologioInCorsa && useIsMobile(768)` il corto circuito salterebbe la
   * chiamata, e l'ordine degli hook cambierebbe a ogni avvio dell'orologio.
   */
  const [orologioInCorsa, setOrologioInCorsa] = useState(false);
  const suTelefono = useIsMobile(768);
  const orologioATuttoSchermo = orologioInCorsa && suTelefono;
  const preferredVariant = usePreferencesStore((s) => s.preferredVariant);
  /* La cadenza preferita: qui non c'e' nessuna card dei punteggi che ne scelga
     una, e il bucket della lista affiancata ne ha bisogno. La variante invece la
     dice il menu «Gioca» accanto. */
  const preferredCadence = usePreferencesStore((s) => s.preferredCadence);

  // Resolution state of the shared daily-puzzle link captured at the top of this
  // component. 'resolving' → neutral spinner on the invite screen, 'gate' → the
  // sign-up teaser, 'error-link' → the unavailable card, 'idle' → normal app.
  const [dailyDeepLinkState, setDailyDeepLinkState] = useState<
    'idle' | 'resolving' | 'gate' | 'error-link'
  >(dailyDeepLinkVariant ? 'resolving' : 'idle');
  // Bumped by the retry button; it is a dependency of the resolver effect, which
  // is what actually re-runs the resolution (the state flip alone would not).
  const [deepLinkAttempt, setDeepLinkAttempt] = useState(0);
  const dailyDeepLinkDone = useRef(false);
  // Open the daily puzzle: mobile -> overlay sheet, desktop -> full screen.
  const openPuzzle = (id: number, practice?: boolean) => {
    if (puzzleSheet) {
      setPuzzleOverlayId(id);
      setPuzzleOverlayPractice(!!practice);
    } else {
      setSelectedPuzzleId(id);
      setPuzzlePractice(!!practice);
      setScreen('puzzle');
    }
  };
  /**
   * Aprire un problema dalla lista "Problemi del giorno".
   *
   * Su mobile il foglio del puzzle si impila sopra la lista e non smonta
   * niente: basta la strada normale.
   *
   * Sul desktop il problema e' una schermata, e cambiare schermata smonta la
   * dashboard — quindi anche la lista. Il suo unmount consuma la voce di
   * cronologia che si era spinta all'apertura, e farlo NELLO STESSO tick in cui
   * si spinge la nuova schermata fa correre le due cose: il `back()` del
   * consumo si mangia la voce appena spinta e «indietro» esce dall'app invece
   * di tornare al problema. Quindi: prima si chiude la lista, poi si naviga, al
   * tick dopo. Al rientro sulla home la lista si riapre da se', cosi' chiudere
   * il problema riporta alla lista come su mobile.
   */
  const openPuzzleFromDailyList = (id: number, practice?: boolean) => {
    if (puzzleSheet) {
      openPuzzle(id, practice);
      return;
    }
    setShowDailyPuzzles(false);
    setReopenDailyPuzzles(true);
    setTimeout(() => openPuzzle(id, practice), 0);
  };
  // Open the client-side demo puzzle (no auth): mobile -> overlay, desktop -> screen.
  const openDemoPuzzle = () => {
    if (puzzleSheet) {
      setPuzzleOverlayDemo(true);
    } else {
      setPuzzleDemo(true);
      setScreen('puzzle');
    }
  };
  // Post-puzzle conversion CTA. Today only the landing demo can reach it: a
  // real puzzle needs an account, so a guest never finishes one (see the CTA in
  // PuzzlePage). La scelta fra conversione e registrazione nuda resta a
  // `apriRegistrazione`, che e' l'unico posto che la sa prendere — un visitatore
  // del demo non ha niente da conservare, un ospite ha le sue partite. Il foglio
  // del puzzle si smonta PRIMA in ogni caso: sta sopra il modale d'accesso.
  const registerFromPuzzle = () => {
    setPuzzleOverlayId(null);
    setPuzzleOverlayDemo(false);
    setPuzzleDemo(false);
    apriRegistrazione();
  };
  const [replayGameId, setReplayGameId] = useState<string | null>(null);
  const [verifyToken, setVerifyToken] = useState<string | null>(null);
  const [resetToken, setResetToken] = useState<string | null>(null);
  /** Challenge link this visitor arrived with (`/?challenge=<id>`). */
  const [inviteLinkId, setInviteLinkId] = useState<string | null>(null);
  /* Il gettone dell'invito a un torneo, tenuto da parte finche' non serve
     all'iscrizione. Non finisce mai in un log. */
  const [inviteTournamentToken, setInviteTournamentToken] = useState<string | null>(null);
  /* Il gemello per le arene private: il gettone che risparmia la password a
     chi arriva dal link. */
  const [inviteArenaToken, setInviteArenaToken] = useState<string | null>(null);
  /*
   * A QUALE torneo appartiene quel gettone.
   *
   * Senza, il gettone era un valore buono per tutta l'app: aprendo l'invito del
   * torneo X, tornando alla lista e aprendo il torneo privato Y, l'iscrizione a
   * Y partiva con la chiave di X — rifiutata, e da quando il rifiuto fa pulizia
   * cancellava pure l'invito a X, che era valido e di cui quella persona non ha
   * la password. Un gettone e' la chiave di UNA porta.
   */
  const [inviteTournamentId, setInviteTournamentId] = useState<string | null>(null);
  /*
   * Il gettone d'invito e' stato RIFIUTATO: si butta qui, dove vive.
   *
   * Tenerlo sarebbe un vicolo cieco lento: la pagina del torneo da' al gettone
   * la precedenza sulla password, quindi a ogni rientro dalla lista ne
   * rimanderebbe uno morto, bruciando un tentativo per volta — e dopo cinque
   * `checkPrivateAccess` blocca la persona per un quarto d'ora. Succede a
   * chiunque abbia ancora il link vecchio dopo che l'organizzatore ha rifatto
   * le chiavi, cioe' il flusso normale di quella funzione. Si toglie anche da
   * `localStorage`, che lo ripescherebbe per ventiquattr'ore.
   *
   * `useCallback` e non una lambda scritta sul posto: la pagina la tiene fra le
   * dipendenze di un effetto, e un'identita' nuova a ogni render di App
   * rifarebbe girare quell'effetto di continuo.
   */
  const scartaGettoneInvito = useCallback(() => {
    setInviteTournamentToken(null);
    setInviteTournamentId(null);
    dimenticaInvitoInSospeso();
  }, []);

  // Online lobby state

  /**
   * The player whose live game we are watching.
   *
   * Deliberately NOT part of `AppLocation`: adding a field there would change
   * the shape every caller builds. A back/forward that restores `'spectate'`
   * with no target simply falls through to the home fallback, which is the
   * right outcome anyway — the game is probably over by then.
   */
  const [spectateTargetUserId, setSpectateTargetUserId] = useState<string | null>(null);
  /** Set by the challenge modal; consumed on the next tick, once it has unmounted. */
  const [pendingSpectateUserId, setPendingSpectateUserId] = useState<string | null>(null);
  /**
   * Da dove si e' entrati a guardare, cioe' dove si torna uscendo.
   *
   * `null` vuol dire la home, che e' l'uscita di sempre e quella delle porte
   * gia' in produzione (lista online dentro la modale di sfida, su Home e su
   * Dashboard). L'arena e' l'unica porta che ha un ritorno suo.
   *
   * L'arena e' segnata QUI e non ripresa da `selectedArenaId` all'uscita: sono
   * due letture a distanza di minuti, e in mezzo c'e' una schermata intera. Il
   * pezzo di stato che dice «torna al dettaglio» e quello che dice «di
   * quest'arena» devono essere lo stesso, o si puo' tornare al dettaglio di
   * un'altra.
   */
  /*
   * Dove tornare quando si smette di guardare. Porta l'arena E il torneo:
   * il ramo che disegna ciascun dettaglio e' guardato dal proprio id, e senza
   * quello la schermata da sola cade sul ripiego della home.
   */
  const [spectateReturn, setSpectateReturn] = useState<{ screen: AppScreen; arenaId: string | null; tournamentId?: string | null } | null>(null);


  // Auth state
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isGuest = useAuthStore((state) => state.isGuest);
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const loginAsGuest = useAuthStore((state) => state.loginAsGuest);
  const checkAuth = useAuthStore((state) => state.checkAuth);
  // checkAuth() has settled (token restored/refreshed or definitively cleared).
  // The ONLY safe gate for auth-dependent boot work: `isAuthenticated` is
  // persisted while `accessToken` is not, so at mount it says "logged in" for a
  // returning user whose token is still being refreshed.
  const isAuthReady = useAuthStore((state) => state.isAuthReady);
  /* Un accesso in volo, da QUALUNQUE porta: la CTA della hero, il modale, o la
     riga online del foglio di «Gioca». Chi deve aprirne uno nuovo aspetta
     questo, invece di sovrapporsi — il limitatore concede 3 ingressi ospite ogni
     15 minuti per IP. */
  const isAuthLoading = useAuthStore((state) => state.isLoading);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Tournament state
  const tournamentStatus = useTournamentStore((state) => state.status);
  /* Il riepilogo di fine partita: finche' c'e', non si strappa via nessuno. */
  const tournamentGameResult = useTournamentStore((state) => state.gameResult);
  /* Stanza e damiera del torneo: servono a riconoscere una partita RILASCIATA,
     cioe' la schermata da cui nessuno portava via (G6). Stessa forma dei due
     gemelli del multiplayer (`mpRoomId`, `mpHaUnaDamiera`). */
  const torneoRoomId = useTournamentStore((state) => state.currentRoomId);
  const torneoHaUnaDamiera = useTournamentStore((state) => state.boardState !== null);
  const partitaDiTorneoFinitaDaMostrare = useTournamentStore((state) => state.partitaFinitaDaMostrare);
  const tournamentJoined = useTournamentStore((state) => state.isJoined);
  const resetTournament = useTournamentStore((state) => state.reset);
  const loadTournaments = useTournamentStore((state) => state.loadTournaments);

  // Arena state
  const arenaStatus = useArenaStore((state) => state.status);
  const arenaInStoreId = useArenaStore((state) => state.currentArena?.id ?? null);
  const arenaPauseSearch = useArenaStore((state) => state.pauseSearch);
  /* L'arena ISCRITTA, non quella che si sta guardando: la presenza si dichiara
     sempre su quella in cui si e' in gara. */
  const arenaIscrittaId = useArenaStore((state) => state.arenaIscrittaId);
  const arenaJoined = useArenaStore((state) => state.isJoined);
  const arenaReset = useArenaStore((state) => state.reset);

  // Log app mount
  useEffect(() => {
    log.info('App', 'App mounted');
  }, []);

  // Check auth on mount
  useEffect(() => {
    checkAuth().then(() => {
      log.info('App', 'checkAuth done', { authed: useAuthStore.getState().isAuthenticated });
    }).catch((e) => {
      log.warn('App', 'checkAuth threw', e);
      // checkAuth() sets isAuthReady on every terminal path of its own, but a
      // throw would leave it false forever — and anything gated on it (the
      // daily-puzzle deep-link resolver, preferences hydration) would hang.
      useAuthStore.setState({ isAuthReady: true });
    });
  }, [checkAuth]);

  // Track page views
  useEffect(() => {
    log.info('App', 'Screen changed', { screen });
    trackPageView(screen);
    // Self-dedups per tab; placed here (not a mount-only effect) so it still
    // fires once a first-time visitor accepts consent and navigates.
    trackSessionStart();
  }, [screen]);

  /*
   * Registra la posizione di scorrimento della home, per rimetterla al ritorno.
   * Scritta in modo sincrono (senza rAF) così l'ultima posizione prima di una
   * navigazione immediata — un tocco su una partita recente — non si perde.
   *
   * Il contenitore di scorrimento è `<body>`, e l'evento `scroll` di un elemento
   * ORDINARIO non risale né a `document` né a `window`: ascoltare quei due non
   * portava niente. `homeScrollRef.current` restava 0 e il ripristino riportava
   * sempre in cima — mentre il `useLayoutEffect` che ripristina scrive
   * `document.body.scrollTop`, cioè SAPEVA dov'è lo scorrimento, e lo dichiara nel
   * proprio commento. `ArenaDetailPage` lo aveva già capito, con la nota
   * «lo scroll della web app sta su document.body, non su window».
   *
   * Perché `<body>` è un contenitore di scorrimento: `html, body { height: 100% }`
   * più `overflow-x: hidden` (index.css) fanno calcolare `overflow-y: auto`.
   *
   * I tre listener restano tutti: `window`/`document` non costano niente e coprono
   * il caso in cui un domani il contenitore tornasse a essere il documento.
   */
  useEffect(() => {
    if (screen !== 'home') return;
    const record = () => {
      homeScrollRef.current = Math.max(
        window.scrollY,
        document.documentElement.scrollTop,
        document.body.scrollTop,
      );
    };
    window.addEventListener('scroll', record, { passive: true });
    document.addEventListener('scroll', record, { passive: true });
    document.body.addEventListener('scroll', record, { passive: true });
    return () => {
      window.removeEventListener('scroll', record);
      document.removeEventListener('scroll', record);
      document.body.removeEventListener('scroll', record);
    };
  }, [screen]);

  // On screen change, restore the home scroll (returning via Back or an in-page
  // back button) or scroll to top (any other screen; deliberate "go home" nav
  // zeroes homeScrollRef so it lands at the top too). A layout effect restores
  // before paint (no flash); the double rAF re-applies after any late reflow
  // (avatars / heatmap) once the dashboard — seeded from cache — is at full height.
  // The scroll container is <body> (overflow-x:hidden + height:100% → overflow-y
  // auto), so window.scrollTo / the scrollingElement (<html>) alone don't move
  // it — write the body too.
  useLayoutEffect(() => {
    const y = screen === 'home' ? homeScrollRef.current : 0;
    const apply = () => {
      window.scrollTo(0, y);
      document.body.scrollTop = y;
      document.documentElement.scrollTop = y;
    };
    apply();
    if (screen !== 'home' || y <= 0) return;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(apply);
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [screen, rulesVariant]);

  /**
   * Route one URL: the shared deep links (utils/share.ts), the emailed token
   * screens, and the localized rules paths.
   *
   * Runs at mount for the URL this visit arrived on, AND again for every later
   * link the installed PWA captures while already running (see useLaunchHandler).
   * That second caller is why the puzzle and editor branches re-arm state the
   * mount path gets for free from the first-render capture: skip that and a
   * second tap on a shared link is swallowed in silence.
   *
   * @returns whether the URL was recognised — the launch handler falls back to a
   * real navigation when it wasn't.
   */
  const applyDeepLink = useCallback((href: string): boolean => {
    const url = new URL(href);
    // Strip the payload but KEEP the history idx state (see useBackNavigation).
    const cleanUrl = () =>
      window.history.replaceState(window.history.state ?? { idx: 0 }, '', '/');

    // Shared daily-puzzle deep link: /?puzzle=daily[&variant=…]. Resolved by the
    // auth-gated effect below — the recipient is usually logged out and needs a
    // session before the puzzle can be started.
    const puzzleVariant = parsePuzzleDeepLink(url.searchParams);
    if (puzzleVariant) {
      setDailyDeepLinkFromUrl(puzzleVariant);
      // Same re-arming the retry button does. The attempt bump is NOT redundant
      // with the state above: a second link for the SAME variant leaves
      // dailyDeepLinkVariant untouched, so nothing in the resolver's deps moves
      // and it would never run again.
      dailyDeepLinkDone.current = false;
      setDeepLinkAttempt((n) => n + 1);
      setDailyDeepLinkState('resolving');
      // At mount the `screen` initialiser already chose this; on a later launch
      // nothing else would.
      setScreen('puzzle-invite');
      cleanUrl();
      return true;
    }
    // DEV-ONLY preview deep link: /?puzzle=preview[&variant=…][&date=YYYY-MM-DD]
    // → open the daily-puzzle preview screen. Dead-code-eliminated in prod builds.
    if (import.meta.env.DEV && url.searchParams.get('puzzle') === 'preview') {
      setPreviewVariant(url.searchParams.get('variant') || 'italian');
      setPreviewDate(url.searchParams.get('date'));
      setScreen('puzzle-preview');
      cleanUrl();
      return true;
    }
    // Shared position deep link: /?editor=<fen>&variant=… → the board editor.
    // Resolved by the auth-gated effect below — the editor needs a real account.
    if (url.searchParams.get('editor')) {
      // A malformed link resolves to null and opens nothing, but it still counts
      // as handled: the address bar is cleaned and the visitor stays put rather
      // than being bounced somewhere by a fallback navigation.
      setEditorDeepLink(parseEditorDeepLink(url.searchParams));
      // No setScreen here on purpose: the resolver switches to the editor only
      // once the FEN has actually loaded.
      editorDeepLinkDone.current = false;
      cleanUrl();
      return true;
    }
    // Challenge-link deep link: /?challenge=<id> → the invite screen. Handled
    // before ?game= because a friend arriving from WhatsApp is usually logged
    // out, and this screen owns its own (guest) login flow.
    const invitedChallengeId = url.searchParams.get('challenge');
    if (invitedChallengeId) {
      setInviteLinkId(invitedChallengeId);
      setScreen('challenge-invite');
      cleanUrl();
      return true;
    }
    /*
     * Invito a un torneo: /?tournament=<id>[&t=<gettone>] → la sua pagina.
     * Il gettone si conserva e viaggia con l'iscrizione: e' cio' che permette a
     * chi ha il link di entrare senza sapere la password.
     */
    const invito = parseTournamentDeepLink(url.searchParams);
    if (invito) {
      setSelectedTournamentId(invito.id);
      setInviteTournamentToken(invito.token);
      setInviteTournamentId(invito.id);
      /*
       * L'invito sopravvive alla registrazione e al giro nella posta: fra questo
       * click e il ritorno nell'app ci sono una registrazione, un'email e un
       * altro click, quasi sempre in un'altra finestra aperta dal client di
       * posta. Senza questo ricordo, alla fine del giro nessuno sa piu' dove la
       * persona stava andando — e il giro lo aveva fatto perche' gliel'avevamo
       * chiesto noi. Lo si dimentica appena la pagina si apre con una sessione
       * vera (vedi l'effetto piu' sotto) e all'uscita dall'account.
       */
      salvaInvitoInSospeso(invito.id, invito.token);
      setScreen('tournament-detail');
      cleanUrl();
      return true;
    }
    /*
     * Invito a un'arena: /?arena=<id>[&t=<gettone>] → la sua pagina.
     *
     * Senza `salvaInvitoInSospeso`: quel ricordo e' fatto per sopravvivere al
     * giro della registrazione via email, e oggi lo conosce solo la strada del
     * torneo. Chi apre un'arena privata da non registrato la vede comunque —
     * l'arena si VEDE, e' entrarci che vuole la chiave — e dopo l'accesso
     * ripassa dal link. Estenderlo alle arene e' un cambio a se'.
     */
    const invitoArena = parseArenaDeepLink(url.searchParams);
    if (invitoArena) {
      setSelectedArenaId(invitoArena.id);
      setInviteArenaToken(invitoArena.token);
      setScreen('arena-detail');
      cleanUrl();
      return true;
    }
    // Shared game-replay deep link: /?game=<id> → open that game's replay. The
    // /api/games/:id endpoint is public, so this works for logged-out users.
    // Un `&notes=` in coda e' il residuo dei link mandati prima della
    // migrazione 114: `cleanUrl()` lo butta con tutta la query, e la partita si
    // apre nuda invece di rompersi.
    const sharedGameId = url.searchParams.get('game');
    if (sharedGameId) {
      setReplayGameId(sharedGameId);
      setScreen('games-history');
      cleanUrl();
      return true;
    }
    if (url.pathname === '/verify') {
      const token = url.searchParams.get('token');
      if (!token) return false;
      setVerifyToken(token);
      setScreen('verify-email');
      cleanUrl();
      return true;
    }
    if (url.pathname === '/reset-password') {
      const token = url.searchParams.get('token');
      if (!token) return false;
      setResetToken(token);
      setScreen('reset-password');
      cleanUrl();
      return true;
    }
    // Deep-linked content page: the rules pages, the landing (`/` and `/{lang}/`),
    // the FAQ and the two legal pages. Everything else falls through.
    const parsed = parseContentPath(url.pathname);
    if (parsed) {
      const capitoloTornei = ancoraTorneiDaHash(url.hash);
      // Il path canonico da lasciare in barra: quasi sempre quello di arrivo,
      // tranne quando una vecchia ancora della FAQ ci manda altrove.
      let canonico = url.pathname;
      if (parsed.kind === 'rules') {
        const rules = parsed.rules;
        if (rules.lang !== i18n.language) i18n.changeLanguage(rules.lang);
        if ('variant' in rules) {
          setRulesVariant(rules.variant);
          setScreen(
            rules.openings ? 'rules-openings' : rules.capture ? 'rules-capture' : 'rules-variant'
          );
        } else if ('gameRules' in rules) {
          // La quarta via. Come i tornei, non ha una variante: senza questo
          // ramo cadrebbe nell'`else` e monterebbe l'INDICE, e subito dopo
          // l'effetto che tiene il path allineato alla lingua riscriverebbe
          // l'URL in `/{lang}/{regole}`.
          setScreen('rules-game');
        } else if ('tournaments' in rules) {
          /*
           * Quattro vie, non due. Le regole dei tornei non hanno una variante come
           * l'indice, quindi senza questo ramo cadrebbero nell'`else` qui sotto
           * e monterebbero l'INDICE — e subito dopo l'effetto che tiene il path
           * allineato alla lingua riscriverebbe l'URL in `/{lang}/{regole}`.
           * Pagina sbagliata e indirizzo perso, senza un errore in console.
           */
          setTournamentAnchor(capitoloTornei);
          setScreen('rules-tournaments');
        } else {
          setScreen('rules-index');
        }
      } else {
        // La radice `/` non dichiara una lingua: chi ci arriva tiene quella che
        // aveva. Solo `/it/`, `/nl/regels` e simili la impongono.
        if (parsed.explicitLang && parsed.lang !== i18n.language) i18n.changeLanguage(parsed.lang);
        if (parsed.page === 'faq' && capitoloTornei) {
          // Il ponte: `…/{faq}#faq-tornei-turni` non ha piu' una voce da
          // aprire qui, ma ha un capitolo dove andare.
          setTournamentAnchor(capitoloTornei);
          setScreen('rules-tournaments');
          canonico = buildTournamentRulesPath(parsed.lang);
        } else {
          setScreen(parsed.page === 'landing' ? 'home' : parsed.page);
        }
      }
      // Unlike the token screens, KEEP the deep URL — it is the canonical
      // location that useBackNavigation should anchor on.
      window.history.replaceState(window.history.state ?? { idx: 0 }, '', canonico);
      return true;
    }
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The URL this visit arrived on (deep links, /verify?token=…, rules pages).
  useEffect(() => {
    applyDeepLink(window.location.href);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Links the installed PWA captures while it is ALREADY running.
  useLaunchHandler(applyDeepLink, arrivalUrl);

  // Keep the URL in sync with the language while on a page that HAS a URL, so
  // switching language updates the localized slug (e.g. /en/rules/turkish-draughts
  // -> /it/regole/dama-turca, and / -> /it/). replaceState (not push) avoids
  // history spam; it doesn't conflict with useBackNavigation since the language
  // change doesn't alter the logical `location`.
  //
  // Deliberately limited to the screens that own a URL: on a game or a profile
  // the address bar is whatever useBackNavigation last pushed, and rewriting it
  // from here would be a second author for the same line.
  /*
   * ATTENZIONE: le schermate di una GARA non vanno mai in questa lista.
   *
   * Qui dentro ci stanno le schermate il cui indirizzo e' un *pathname*
   * localizzato, e l'effetto qui sotto lo tiene allineato confrontando
   * `window.location.pathname` con il percorso atteso. L'indirizzo di un torneo
   * e' `/?tournament=<id>`: una QUERY, indipendente dalla lingua, e derivata da
   * un identificativo invece che dalla schermata. Un confronto di `pathname`
   * contro quella forma non combacia MAI, quindi aggiungere `tournament-detail`
   * qui vorrebbe dire un `replaceState` a ogni render. L'indirizzo delle gare lo
   * scrive `locationToUrl`, ed e' il suo unico autore.
   */
  const screenHasOwnUrl =
    screen === 'rules-variant' ||
    screen === 'rules-index' ||
    screen === 'rules-game' ||
    screen === 'rules-openings' ||
    screen === 'rules-capture' ||
    screen === 'rules-tournaments' ||
    screen === 'faq' ||
    screen === 'terms' ||
    screen === 'privacy' ||
    screen === 'home';
  useEffect(() => {
    if (!screenHasOwnUrl) return;
    if (!isRulesLang(i18n.language)) return;
    const path = contentUrlFor(i18n.language, screen, rulesVariant);
    if (window.location.pathname !== path) {
      window.history.replaceState(window.history.state ?? { idx: 0 }, '', path);
    }
  }, [i18n.language, screen, rulesVariant, screenHasOwnUrl]);

  /*
   * La testa del documento, decisa in un posto solo. Le schermate senza un URL
   * proprio ricadono sulla landing, che e' l'indirizzo su cui stanno davvero:
   * prima di questa riga, uscire da una pagina regole lasciava in testa il suo
   * canonical e i suoi hreflang, che continuavano a dichiarare una pagina che
   * l'utente aveva gia' lasciato.
   */
  const headTarget = useMemo<HeadTarget>(() => {
    switch (screen) {
      case 'rules-game':
        return { page: 'rules-game' };
      case 'rules-index':
        return { page: 'rules', variant: null };
      case 'rules-variant':
        return { page: 'rules', variant: rulesVariant };
      case 'rules-openings':
        return { page: 'rules', variant: rulesVariant, openings: true };
      case 'rules-capture':
        return { page: 'rules', variant: rulesVariant, capture: true };
      case 'rules-tournaments':
        return { page: 'rules-tournaments' };
      case 'faq':
      case 'terms':
      case 'privacy':
        return { page: screen };
      default:
        return { page: 'landing' };
    }
  }, [screen, rulesVariant]);
  usePageHead(headTarget);

  // Local game state
  /* Reattivo, e serve a una cosa sola: ricalcolare «la schermata sotto ha una
     partita viva?» per `useStudyHandoff`. Gli altri lo leggono con `getState()`
     perche' lo guardano da dentro un gestore di eventi. */
  const localGameStatus = useGameStore((state) => state.status);
  const startLocalGame = useGameStore((state) => state.startLocalGame);
  const startLocalGameFromBoard = useGameStore((state) => state.startLocalGameFromBoard);
  const setVariant = useGameStore((state) => state.setVariant);
  const setTimeControl = useGameStore((state) => state.setTimeControl);

  // Multiplayer state (only status/connect/disconnect needed - OnlineGame handles the rest)
  const mpStatus = useMultiplayerStore((state) => state.status);
  const mpConnect = useMultiplayerStore((state) => state.connect);
  const mpDisconnect = useMultiplayerStore((state) => state.disconnect);
  const mpRoomId = useMultiplayerStore((state) => state.roomId);
  const mpHaUnaDamiera = useMultiplayerStore((state) => state.boardState !== null);
  const partitaTerminataMentreEriVia = useMultiplayerStore((state) => state.partitaTerminataMentreEriVia);
  const clearPartitaTerminataMentreEriVia = useMultiplayerStore((state) => state.clearPartitaTerminataMentreEriVia);
  /* Sedersi al tavolo che un collegato ha gia' aperto. Vive qui e scende per
     prop — nella lista dei collegati e nella dashboard — perche' ne' il modale
     ne' la home conoscono il lobby store, e tirarglielo dentro farebbe passare
     mezzo `services/socket` in ogni test che li monta. */
  const sitAtLobbyTable = useMultiplayerStore((state) => state.sitAtLobbyTable);

  // Pending challenge/rematch invites. Read here only to know whether the app is
  // idle enough to swap in a new build (see `safeToReload` below); the overlays
  // that render them are separate components further down.
  const outgoingChallenge = useOnlineStore((state) => state.outgoingChallenge);
  const incomingChallenge = useOnlineStore((state) => state.incomingChallenge);
  const incomingRematch = useOnlineStore((state) => state.incomingRematch);
  const linkChallenge = useOnlineStore((state) => state.linkChallenge);
  const pendingLinkChallenge = useOnlineStore((state) => state.pendingLinkChallenge);

  // Log auth state changes
  useEffect(() => {
    if (isAuthenticated) {
      log.info('App', 'Auth state changed', { state: isGuest ? 'guest' : 'logged-in' });
    } else {
      log.info('App', 'Auth state changed', { state: 'logged-out' });
    }
  }, [isAuthenticated, isGuest]);

  // Auto-connect socket when authenticated (enables auto-rejoin)
  // Reconnect when user identity changes (e.g. guest → registered login)
  const userId = user?.id;
  const prevUserIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (isAuthenticated) {
      // If user identity changed (guest→registered), force reconnect with new credentials
      if (prevUserIdRef.current && prevUserIdRef.current !== userId) {
        mpDisconnect();
      }
      prevUserIdRef.current = userId;
      mpConnect();
    }
  }, [isAuthenticated, userId, mpConnect, mpDisconnect]);

  // Log socket status changes
  useEffect(() => {
    if (mpStatus === 'connected') {
      log.info('App', 'Socket connected');
    } else if (mpStatus === 'disconnected') {
      log.info('App', 'Socket disconnected');
    }
  }, [mpStatus]);

  // (La strada opposta — uscire quando il rientro ha RILASCIATO la partita —
  // sta piu' in basso, subito dopo `leaveGameTo`, che le serve.)
  // When multiplayer game starts or rejoins, switch to game screen.
  // Navigate from any non-game screen (e.g. accepting a direct challenge
  // from the rules page, profile, leaderboard, ...), not just lobby/home.
  useEffect(() => {
    const activeGameScreens: AppScreen[] = ['online-game', 'tournament-game', 'arena-game'];
    if (mpStatus === 'playing' && !activeGameScreens.includes(screen)) {
      setScreen('online-game');
    }
  }, [mpStatus, screen]);

  // Handle tournament status changes
  useEffect(() => {
    // Alla partita si va da QUALUNQUE schermata, non solo dalle due del torneo.
    //
    // Il server accoppia chi e' connesso, non chi sta guardando la pagina del
    // torneo (`findSocketByUserId` scorre i socket e chiede solo che siano
    // autenticati). Con il filtro vecchio — lobby e dettaglio — chi aspettava il
    // turno sulla Home, sui Puzzle, sulle Regole o perfino sull'ELENCO dei tornei
    // veniva accoppiato e non lo scopriva mai: 25 schermate su 28. La partita
    // esisteva, l'orologio partiva alla prima mossa dell'avversario, e lui
    // guardava altro. Il multiplayer (qui sopra) e l'arena navigano gia' cosi'.
    //
    // E da quando il rientro dopo una ricarica e' delegato allo store dei tornei,
    // questo e' l'UNICO navigatore rimasto: il multiplayer non passa piu' a
    // 'playing' per una partita di torneo, quindi il suo effetto non scatta.
    const activeGameScreens: AppScreen[] = ['online-game', 'tournament-game', 'arena-game'];
    /* La regola del dopo-torneo sta in `doveRimbalzaLaSchermataDelTorneo`, e non
       piu' qui: accanto le vive la gemella che decide le voci di cronologia
       ripristinabili (`doveAtterraLaVoceDelTorneo`, letta da `applyLocation`), e
       un test fissa che la seconda copra sempre la prima. Divergendo tornerebbe
       il rimbalzo, cioe' una voce che si riscrive da se'. */
    const rimbalzoDelDopoTorneo = doveRimbalzaLaSchermataDelTorneo({
      screen, tournamentStatus, gameResult: tournamentGameResult,
    });
    /*
     * La rete: chi ha una partita di TORNEO in corso ma sta guardando la
     * schermata online va riportato indietro.
     *
     * `activeGameScreens` qui sotto serve a non strappare via chi gioca, ma
     * include `online-game` — e quindi, una volta finiti li', non se ne usciva
     * mai piu'. Ci si finiva dal rientro in grazia, che consegnava al
     * multiplayer anche le partite di torneo (riparato nel commit che precede);
     * questa riga vale per qualunque altra strada porti allo stesso posto.
     */
    if (laPartitaDiTorneoEInUnaSchermataSbagliata(screen, tournamentStatus)) {
      setScreen('tournament-game');
    } else if (tournamentStatus === 'playing' && !activeGameScreens.includes(screen)) {
      // Come fa l'arena: chi viene strappato dallo spettatore deve DIMENTICARE
      // chi stava guardando, altrimenti resta una voce di cronologia 'spectate'
      // che a partita finita riapre la partita di un altro invece della classifica.
      if (screen === 'spectate') setSpectateTargetUserId(null);
      setScreen('tournament-game');
    } else if (tournamentStatus === 'match-found' && screen === 'tournament-detail') {
      /*
       * Solo `match-found`, cioe' l'ISTANTE dell'accoppiamento: e' arrivato un
       * avversario e la stanza del torneo ha la finestra che lo annuncia.
       *
       * `waiting` era qui dentro fino al 20 Set 2026, e rendeva la SCHEDA del
       * torneo irraggiungibile a chi e' iscritto: quello stato dura per tutto
       * il torneo (`loadTournament` lo mette a chiunque sia iscritto a un
       * torneo attivo), quindi la condizione non scattava all'iscrizione ma a
       * OGNI render — aprivi il tuo torneo dall'elenco e venivi sbattuto in
       * lobby, tornavi indietro dalla lobby e ci venivi risbattuto.
       *
       * Il guaio non era la pagina in se': e' che l'occhio dello spettatore
       * sta nella classifica della scheda, e chi aspetta il turno successivo e'
       * esattamente la persona che vuole guardare la partita di un altro. La
       * sola che non riusciva ad arrivarci.
       *
       * L'iscrizione non perde niente: chi si iscrive a un torneo ATTIVO viene
       * portato in lobby da `onJoin`, esplicitamente, in
       * `TournamentDetailPage.handleJoin` — e per un torneo non ancora partito
       * resta sulla scheda, che e' sempre stato il comportamento voluto.
       */
      setScreen('tournament-lobby');
    } else if (rimbalzoDelDopoTorneo) {
      /*
       * A TORNEO FINITO si va alla scheda, non alla lobby.
       *
       * FIX-37, che questo ramo eredita: non si viene strappati dalla schermata
       * di partita mentre il riepilogo e' ancora a schermo. Vincendo l'ultima
       * partita dello svizzero serale il risultato arrivava, il torneo veniva
       * marcato finito e l'utente spariva di li' PRIMA di vedere punti e
       * variazione di rating. Il riepilogo si chiude col suo bottone, e solo
       * allora ci si muove.
       *
       * La novita' e' dove si va, e che ci si va anche DALLA LOBBY. La lobby e'
       * la stanza di chi gioca e col torneo finisce: a torneo chiuso mostrava le
       * stesse tre schede della scheda del torneo — classifica, partite,
       * dettagli — piu' la fascia del vincitore, cioe' una pagina in piu' che
       * diceva le stesse cose. Ora la fascia sta nella scheda, e la scheda e'
       * l'unica pagina del dopo-torneo: quella che si riapre dalla lista, da un
       * link, e che il vincitore lo dice anche a chi alla fine non c'era.
       *
       * Prima l'id, poi la schermata, come per il ritorno dallo spettatore: il
       * ramo che disegna il dettaglio e' guardato da `selectedTournamentId`, e
       * senza quello la pagina cadrebbe sul ripiego invece di aprirsi. Chi
       * arriva qui dalla lobby l'id ce l'ha quasi sempre gia'; chi ci rientra
       * dopo una ricarica no, e lo store e' l'unico a saperlo.
       */
      const idDelTorneo = useTournamentStore.getState().currentTournament?.id;
      if (idDelTorneo) setSelectedTournamentId(idDelTorneo);
      setScreen(rimbalzoDelDopoTorneo);
    }
  }, [tournamentStatus, screen, tournamentGameResult]);

  /*
   * La partita di torneo finita mentre si era via (vedi
   * `mostraPartitaFinitaMentreEriVia`): la si mostra, col suo esito, da
   * qualunque schermata ci si trovi. L'effetto qui sopra non basta: guida
   * verso la partita solo quando lo stato e' 'playing', e questa e' gia' in
   * 'waiting'. Il riepilogo resta a schermo finche' c'e' `gameResult` (FIX-37),
   * e da li' «Continua» porta al torneo come dopo ogni altra partita.
   */
  useEffect(() => {
    if (!partitaDiTorneoFinitaDaMostrare) return;
    if (screen !== 'tournament-game') setScreen('tournament-game');
    useTournamentStore.getState().consumaPartitaFinitaDaMostrare();
  }, [partitaDiTorneoFinitaDaMostrare, screen]);

  /*
   * L'arena in cui si è davvero, secondo lo store, VALORIZZA `selectedArenaId`
   * quando è vuoto — non lo sovrascrive quando c'è.
   *
   * Il motivo per cui esiste resta quello di sempre: dopo un rientro automatico
   * (`rejoinGame`) nessuno lo valorizzava, e uscendo dalla partita la pagina di
   * dettaglio non poteva essere renderizzata (serve `selectedArenaId`), quindi
   * si finiva sul fallback Home. Lì il campo è NULLO, e questa riga lo riempie.
   *
   * Sovrascriverlo anche quando c'è già voleva dire dirottare una scelta
   * esplicita: chi era iscritto toccava la card di «Rapid 20:00» e gli si
   * apriva «Blitz 18:00», senza un messaggio — e non solo mentre era in coda,
   * ma anche solo essendo iscritto a un'arena della sera, per tutto il
   * pomeriggio. La regola voluta è un'altra, e sta dove si decide: da un'arena
   * IN CORSO non ci si iscrive a un'altra (`joinArena`, arena-store). Guardare
   * resta libero.
   */
  useEffect(() => {
    if (arenaInStoreId && !selectedArenaId) {
      setSelectedArenaId(arenaInStoreId);
    }
  }, [arenaInStoreId, selectedArenaId]);

  /*
   * In coda solo dentro la sezione arena: la regola guarda dove sei finito,
   * non da che porta sei uscito. Stava in `handleNavigate`, che copriva la
   * sola navigazione e lasciava scoperti il gesto indietro e i salti laterali
   * (il tocco su un nome in classifica, per dirne uno).
   */
  useArenaQueueGuard(arenaStatus, screen, arenaPauseSearch);

  /*
   * Due regole, due hook, e la divisione dei compiti e' voluta.
   *
   * `useArenaQueueGuard` qui sopra e' la regola di SCHERMATA: sei in coda ma non
   * sei nella sezione arena, quindi esci dalla coda. Vale solo per chi e' in
   * coda, ed e' una decisione presa dal client.
   *
   * `useArenaPresence` e' la regola di VISIBILITA': stai guardando lo schermo,
   * oppure no. Vale per chiunque sia iscritto — partita in corso compresa,
   * perche' il caso peggiore e' proprio l'app messa via mentre si gioca — e non
   * decide niente: dichiara al server, che tiene l'attesa sul suo giro di
   * accoppiamento. Prima quella decisione stava in un `setTimeout` dentro la
   * pagina, e una pagina in secondo piano e' esattamente cio' che il sistema
   * operativo puo' sospendere.
   */
  useArenaPresence(arenaIscrittaId, arenaStatus);

  /*
   * Tornati in primo piano, si sveglia il socket.
   *
   * Non c'era nessuno a dire «riprova adesso»: si aspettava il timer di ping di
   * engine.io e poi il backoff, e su un grace period di 30 secondi ogni secondo
   * bruciato ad aspettare è tolto dal budget di rientro. Su iOS è peggio: il
   * WebSocket muore in silenzio e `socket.connected` resta `true`, quindi
   * nessuna riconnessione parte affatto.
   *
   * Il risveglio è una sonda che, se il socket è vivo, porta con sé il resync
   * completo — scacchiera, orologio, conversazione. Non ferma e non recupera
   * nessun orologio: il tempo passato in secondo piano è corso, e doveva
   * correre.
   */
  useVisibility({
    onVisible: () => {
      void risvegliaSocket();
    },
  });

  /*
   * E il verso opposto: quando il dispositivo dichiara di NON avere rete, i
   * tavoli della lobby e la lista dei collegati scadono subito, senza aspettare
   * che il socket se ne accorga da solo (fino a ~45 s di battito, piu' l'attesa
   * di sicurezza). Senza questo, un telefono portato fuori rete continuava a
   * mostrare i tavoli e i giocatori dell'ultima connessione.
   */
  useScadenzaPresenza();

  /*
   * E chiusa la pagina dei risultati l'arena si archivia, o il suo 'finished'
   * rimanderebbe al podio la prossima arena che si apre. Stessa regola,
   * stessa forma: conta lo schermo in cui si finisce, non la porta.
   */
  useArenaResultsCleanup(arenaStatus, screen, arenaReset);

  /*
   * E lo stesso per il TORNEO, che non ce l'aveva: lasciata la scheda di un
   * torneo concluso lo store si archivia. Senza, `status: 'finished'` e il
   * torneo in memoria restavano in canna per tutta la seduta — e non e' solo la
   * navigazione a leggerli.
   */
  useArchiviazioneTorneo(tournamentStatus, screen, resetTournament);


  // Single entry point to the profile screen: keeps `profileTab` in sync so a
  // previous "open on Settings" never leaks into a later, plain profile visit.
  const openProfile = useCallback((name: string, tab: string | null = null) => {
    setProfileUsername(name);
    setProfileTab(tab);
    setScreen('profile');
  }, []);

  // Same idea as openProfile, for the leaderboard: the tab/variant asked for by
  // one entry point must never leak into a later, plain visit from the menu.
  const openLeaderboard = useCallback((
    tab: string | null = null,
    variant: string | null = null,
    /* `undefined` = nessuna finestra chiesta (la Classifica usa la sua);
       `null` = «di sempre», che e' una richiesta come le altre. */
    days: number | null | undefined = undefined,
  ) => {
    setLeaderboardTab(tab);
    setLeaderboardVariant(variant);
    setLeaderboardPeriod(days);
    setScreen('leaderboard');
  }, []);

  // Stessa regola di openProfile e openLeaderboard: la voce chiesta da UNA porta
  // (la card «Installa l'app», che manda alle istruzioni del sistema operativo)
  // non deve restare appiccicata alla visita successiva fatta dal footer.
  const openFaq = useCallback((entryId: string | null = null) => {
    setFaqEntry(entryId);
    setScreen('faq');
  }, []);

  // Stessa forma e stessa ragione di openFaq: i cinque aiuti dentro i tornei
  // chiedono UN capitolo, e quel capitolo non deve restare appiccicato a chi
  // arriva dopo dall'indice delle regole per leggere il regolamento dall'inizio.
  const openTournamentRules = useCallback((anchor: TournamentRulesAnchor | null = null) => {
    setTournamentAnchor(anchor);
    setScreen('rules-tournaments');
  }, []);

  /*
   * Una riga del foglio: prima si chiude il foglio, poi si va.
   *
   * Chiudere un overlay manda indietro la cronologia (`consumeOverlay` ->
   * `history.back()`), e quel back e' ASINCRONO: aprire la destinazione — o
   * peggio cambiare schermata, che spinge una voce nuova — mentre quel back e'
   * ancora in volo gli mette la voce nuova sotto il naso, e se la mangia. Il
   * sintomo e' che il primo «indietro» dopo aver toccato una riga esce
   * dall'app. `runAfterProgrammaticPops` (useOverlayBackButton) e' il rimedio:
   * esegue quando ogni riavvolgimento e' arrivato davvero.
   *
   * Ma va armato AL MOMENTO GIUSTO, ed e' il passaggio che sembra un dettaglio
   * e non lo e'. `setFoglioGiocaAperto(false)` non chiude niente subito:
   * pianifica un render. Chiamando l'aiutante sulla riga dopo, il contatore dei
   * riavvolgimenti e' ancora a zero — nessuno li ha chiesti — e lui esegue
   * SUBITO, cioe' esattamente nel commit sbagliato. Verificato a mano: il
   * foglio si chiudeva, il modale d'accesso si apriva, e il primo «indietro»
   * usciva dall'app.
   *
   * Quindi la destinazione si mette da parte qui e si arma nell'effetto qui
   * sotto, che gira DOPO la pulizia dell'overlay chiuso — cioe' dopo che il suo
   * `history.back()` e' stato chiesto.
   */
  const azioneDopoIlFoglio = useRef<(() => void) | null>(null);
  const dalFoglioGioca = (vai: () => void) => {
    azioneDopoIlFoglio.current = vai;
    setFoglioGiocaAperto(false);
  };
  /**
   * Lo stato della PRIMA riga del foglio, quella che porta online.
   *
   * 'attesa'  = non c'e' ancora una decisione su chi siamo (`checkAuth()` in
   *             volo), oppure un accesso chiesto ALTROVE — la CTA della hero, il
   *             modale — e' in corso: si aspetta quello, non se ne apre un altro.
   * 'accesso' = l'ingresso da ospite nostro e' in volo.
   *
   * Due stati e non uno, perche' l'effetto che riprende l'attesa deve poter
   * distinguere «da riprendere» da «gia' in volo»: confusi, ripartirebbe sopra
   * un accesso in corso, cioe' due POST su un limitatore che ne concede 3 ogni
   * 15 minuti per IP. A schermo sono la stessa riga, che dice «Connessione...».
   */
  const [faseOnline, setFaseOnline] = useState<'ferma' | 'attesa' | 'accesso'>('ferma');
  /** La CHIAVE i18n del motivo del rifiuto, mostrata SOTTO la riga che l'ha chiesto. */
  const [erroreOspiteDalFoglio, setErroreOspiteDalFoglio] = useState<string | null>(null);
  /**
   * I problemi del giorno rifiutano chi non ha un conto, e il rifiuto e' un
   * OVERLAY: `OverlaySoloIscritti`, qui sotto fra gli overlay globali.
   *
   * Non e' un errore come quello qui sopra: li' c'e' una richiesta che non e'
   * andata e si puo' riprovare, qui c'e' una porta che a chi non ha un conto non
   * si apre. C'e' stato un tempo in cui lo diceva un riquadro DENTRO il foglio
   * di «Gioca»: era la quinta riga di sei, e a 360x640 doveva portarsi in vista
   * da se' per farsi leggere. Ora e' la stessa forma della sfida diretta, che
   * per lo stesso rifiuto alza una scheda da sempre.
   */
  const [puzzleSoloIscritti, setPuzzleSoloIscritti] = useState(false);
  /*
   * La corsa in volo, e perche' e' un contatore e non una bandierina.
   *
   * Chiudere il foglio mentre l'ingresso da ospite e' in volo ABBANDONA la
   * corsa. Senza, la risposta atterrerebbe su un foglio gia' chiuso e
   * chiamerebbe `dalFoglioGioca`, che mette da parte una destinazione e chiude
   * un foglio GIA' chiuso: l'effetto qui sotto gira sul CAMBIO di
   * `foglioGiocaAperto`, quindi non gira. Il lobby non si aprirebbe adesso, e si
   * aprirebbe da solo alla prima chiusura successiva, qualunque fosse.
   */
  const corsaOspite = useRef(0);
  useEffect(() => {
    // Solo alla chiusura, e solo se c'era una riga dietro: la X, il velo e il
    // gesto indietro chiudono il foglio e basta.
    if (foglioGiocaAperto) return;
    /*
     * Il foglio chiuso abbandona la corsa dell'ospite e si dimentica il suo
     * stato: la riga che aspettava e il motivo dell'ultimo rifiuto non devono
     * ritrovarsi addosso al foglio riaperto. Qui e non nel gestore della X,
     * perche' le uscite sono QUATTRO — X, velo, gesto indietro, cambio
     * schermata — e tre di loro non passano da nessun gestore di questo file.
     * E prima della lettura di `vai`: il ramo riuscito chiama `dalFoglioGioca`
     * a corsa gia' finita, quindi invalidarla qui non lo disturba.
     */
    corsaOspite.current += 1;
    setFaseOnline('ferma');
    setErroreOspiteDalFoglio(null);
    const vai = azioneDopoIlFoglio.current;
    if (!vai) return;
    azioneDopoIlFoglio.current = null;
    runAfterProgrammaticPops(vai);
  }, [foglioGiocaAperto]);

  /*
   * Lo stesso per il foglio di «Strumenti», e per la stessa ragione: le tre
   * righe portano a una SCHERMATA, cioe' una voce nuova in cronologia, e
   * scriverla mentre il `history.back()` della chiusura e' ancora in volo se la
   * mangia. La spiegazione per esteso sta sopra `dalFoglioGioca`; qui sotto c'e'
   * solo la copia che serve al secondo foglio.
   *
   * Niente da dimenticare alla chiusura, al contrario del foglio di «Gioca»:
   * nessuna di queste tre righe ha un'attesa o un errore da azzerare, perche'
   * nessuna delle tre passa dalla rete.
   */
  const azioneDopoStrumenti = useRef<(() => void) | null>(null);
  const dalFoglioStrumenti = (vai: () => void) => {
    azioneDopoStrumenti.current = vai;
    setStrumentiAperto(false);
  };
  useEffect(() => {
    if (strumentiAperto) return;
    const vai = azioneDopoStrumenti.current;
    if (!vai) return;
    azioneDopoStrumenti.current = null;
    runAfterProgrammaticPops(vai);
  }, [strumentiAperto]);

  /*
   * «Gioca subito», la prima riga del foglio — e perche' non e' piu' un muro.
   *
   * Qui c'era `if (isAuthenticated) … else apriAccesso()`, e la ragione scritta
   * accanto era vera a META': aprire il lobby A SECCO a un visitatore senza
   * sessione gli da' una cadenza che non fa niente. Quella meta' vale ancora, ed
   * e' il motivo per cui il lobby si apre solo con un token in mano.
   *
   * Ma la cura era la porta sbagliata. Il server lascia giocare l'ospite — gli
   * vieta i soli tavoli `matchType: 'rated'` (`canJoinMatchType`, in
   * packages/shared/src/types/challenge.ts) — e la hero della landing, con lo
   * STESSO testo «Gioca subito», gli crea la sessione e lo porta dentro
   * (`HomePage.handlePlayAsGuestClick`). Due porte col medesimo nome e due esiti
   * diversi: chi entrava dalla barra trovava un modale d'accesso che la hero non
   * gli aveva mai chiesto. Ora la sessione si crea PRIMA del lobby.
   */
  /*
   * L'unica strada dal foglio al lobby, e serve a tenere UNA sola
   * `setShowLobbyOverlay(true)` in tutto questo giro: i tre rami che portano
   * dentro — sessione gia' in mano, ospite appena creato, sessione arrivata da
   * se' mentre si aspettava — sarebbero tre posti in cui scordare
   * `dalFoglioGioca`, e tre volte in cui il primo «indietro» esce dall'app.
   */
  const vaiAlLobbyDalFoglio = () => {
    dalFoglioGioca(() => setShowLobbyOverlay(true));
  };

  /** Crea la sessione ospite e porta al lobby; se non ce la fa, lo DICE. */
  const entraComeOspiteEVaiAlLobby = async () => {
    const corsa = ++corsaOspite.current;
    setFaseOnline('accesso');
    trackGuestPlayClicked('foglio-gioca');
    const chiave = await handlePlayAsGuest();
    /*
     * Il foglio e' stato chiuso nel frattempo: la sessione ospite RESTA — e'
     * un ingresso riuscito, non un rifiuto — ma il lobby no. Non si strappa a
     * nessuno una pagina che non ha piu' chiesto.
     */
    if (corsa !== corsaOspite.current) return;
    setFaseOnline('ferma');
    if (chiave) {
      setErroreOspiteDalFoglio(chiave);
      return;
    }
    vaiAlLobbyDalFoglio();
  };

  const apriGiocaOnline = () => {
    setErroreOspiteDalFoglio(null);
    // Una sessione c'e' gia', registrata o ospite: il lobby nello stesso commit
    // del tocco, esattamente come prima.
    if (isAuthenticated) {
      vaiAlLobbyDalFoglio();
      return;
    }
    // Un tentativo alla volta (la riga e' anche `disabled`, ma la guardia sta
    // dove sta la decisione).
    if (faseOnline !== 'ferma') return;
    /*
     * E non si decide «e' un ospite» prima che l'accesso sia stato DECISO:
     * `accessToken` non e' persistito, quindi un utente registrato passa da una
     * finestra in cui `isAuthenticated` e' falso mentre `checkAuth()` e' in
     * volo, e `loginAsGuest()` gli scriverebbe sopra la sessione. La regola sta
     * scritta per intero piu' sotto, sui deep link.
     *
     * `isAuthLoading` tiene la stessa riga per un motivo diverso: un visitatore
     * puo' aver premuto la CTA della hero (che si spegne) e POI aperto il
     * foglio. In entrambi i casi la riga ASPETTA e l'effetto qui sotto riprende:
     * un tocco che non fa niente e' il difetto da cui siamo partiti, non la cura.
     */
    if (!isAuthReady || isAuthLoading) {
      setFaseOnline('attesa');
      return;
    }
    void entraComeOspiteEVaiAlLobby();
  };

  // La ripresa dell'attesa: ora si sa chi siamo, e nessun altro accesso e' in volo.
  useEffect(() => {
    if (faseOnline !== 'attesa' || !isAuthReady || isAuthLoading) return;
    /*
     * E solo col foglio ancora su: la chiusura e `isAuthReady` possono cadere
     * nello stesso commit, e l'effetto della chiusura — che gira prima di questo
     * — azzera `faseOnline` con un `set` che questo render non vede ancora.
     * Senza questa riga il lobby si aprirebbe sopra un foglio gia' chiuso.
     */
    if (!foglioGiocaAperto) return;
    // La sessione e' arrivata da se' (token rinnovato, o accesso fatto altrove):
    // nessun ospite da creare, e soprattutto nessuno da sovrascrivere.
    if (isAuthenticated) {
      setFaseOnline('ferma');
      vaiAlLobbyDalFoglio();
      return;
    }
    void entraComeOspiteEVaiAlLobby();
    // Le tre funzioni qui sopra si ridefiniscono a ogni render e non hanno stato
    // proprio: fra le dipendenze rifarebbero l'accesso a ogni render. La guardia
    // vera e' `faseOnline`, che cambia una volta sola.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [faseOnline, isAuthReady, isAuthLoading, isAuthenticated, foglioGiocaAperto]);

  /*
   * «Problemi del giorno»: la lista e' montata da DashboardPage, che vive solo
   * sulla home — quindi ci si passa, e lo stato che la apre e' gia' qui apposta
   * (vedi `showDailyPuzzles`). Dal foglio si arriva da qualsiasi schermata, non
   * solo dalla home come il bottone della hero.
   */
  const apriProblemiDelGiorno = () => {
    homeScrollRef.current = 0;
    setScreen('home');
    setShowDailyPuzzles(true);
  };

  /*
   * Dove porta una voce della barra. Il gestore vero e' `handleNavigate` qui
   * sotto: questa e' solo la destinazione, separata da lui perche' col foglio
   * di «Gioca» aperto ci si arriva per un'altra strada (vedi li').
   */
  const vaiAllaVoce = (item: NavItem) => {
    /*
     * Nessun ramo `'play'`, e non e' una dimenticanza: quella voce non e' una
     * destinazione ma un interruttore, e `handleNavigate` la ferma prima, a
     * ogni larghezza. Il ramo c'era finche' il foglio esisteva solo sul
     * telefono e sul desktop la voce andava dritta al lobby — allora la hero
     * della dashboard mostrava le sei porte, e un menu intermedio sarebbe stato
     * un'anticamera fra il gesto e la cosa. Ora le hero hanno un invito solo e
     * le porte stanno tutte nel foglio: il lobby resta a un gesto, ma sono la
     * prima riga del foglio e il «Gioca» della hero, non questa funzione.
     */
    switch (item) {
      case 'home':
        // Deliberate "Home" click → start at the top, not a restored position.
        homeScrollRef.current = 0;
        setScreen('home');
        break;
      case 'arena':
        /*
         * L'id e' `'arena'`, la voce si chiama «Tornei» e porta ai TORNEI: e'
         * la stessa coppia che regge `getActiveNavItem`, dove `'arena'` e'
         * l'id che si accende su tutte le gare — arene e tornei insieme.
         * Rinominarlo vorrebbe dire riscrivere quella corrispondenza per non
         * cambiare niente a schermo.
         *
         * Prima apriva un foglio con dentro le due liste, tre card per scheda,
         * e per arrivare ai tornei ci volevano altri due tocchi. Poi la porta
         * e' diventata la cosa stessa, e l'elenco delle arene un richiamo in
         * fondo. Ora non c'e' piu' nemmeno quello: le arene SONO righe
         * dell'elenco dei tornei, sotto il filtro «Formato», e una schermata
         * `'arena'` non esiste piu'.
         */
        setScreen('tournaments');
        break;
      case 'tournaments':
        // Tournaments viewable without login (participation requires login)
        setScreen('tournaments');
        break;
      case 'puzzle':
        setScreen('puzzle');
        break;
      case 'leaderboard':
        openLeaderboard();
        break;
      case 'faq':
        openFaq();
        break;
      case 'rules':
        setScreen('rules-index');
        break;
      case 'profile':
        // Profile only shown when authenticated (handled in Sidebar)
        if (isAuthenticated && user) {
          openProfile(user.username);
        }
        break;
    }
  };

  /*
   * Il tocco su una voce della barra, e le due cose che succedono PRIMA della
   * destinazione.
   *
   * 1. «Gioca» non e' una schermata: e' un interruttore. Apre il foglio, e un
   *    secondo tocco lo richiude — e' il bottone che e' rimasto visibile
   *    apposta (la barra ora sta sopra il velo, FoglioGioca.css), e l'unica
   *    cosa che puo' voler dire premerlo di nuovo. Chiudere cosi' e' la stessa
   *    strada della X e del velo: lo smontaggio di `useOverlayBackButton`
   *    consuma la voce cuscinetto, e la cronologia resta in pari.
   *
   *    Vale a ogni larghezza. Sul desktop la voce andava dritta al lobby,
   *    perche' le sei porte erano tutte nella hero della dashboard: quelle
   *    ridondanze sono andate via, e il foglio e' rimasto l'indice unico.
   *
   * 2. OGNI ALTRA voce, col foglio aperto, e' una destinazione raggiunta DA un
   *    overlay: esattamente il caso per cui `dalFoglioGioca` esiste, e va fatta
   *    passare di li' come le sei righe del foglio.
   *
   *    Senza, il guasto e' quello descritto sopra `dalFoglioGioca`, e arriva per
   *    una strada che sembra innocua: il commit del `setScreen` fa girare
   *    l'osservatore di `useBackNavigation`, che chiede `runAfterProgrammaticPops`
   *    mentre il contatore dei riavvolgimenti e' ANCORA a zero — il foglio si
   *    chiude nel commit dopo — quindi la voce nuova viene scritta subito e il
   *    `history.back()` della chiusura le arriva addosso. L'effetto di sicurezza
   *    su `[screen]` non basta: chiude il foglio, ma troppo tardi per l'ordine
   *    della cronologia.
   */
  const handleNavigate = (item: NavItem) => {
    /*
     * Due voci-interruttore, e l'una chiude l'altra.
     *
     * Aprire il secondo foglio sopra il primo vorrebbe dire DUE voci cuscinetto
     * in cronologia per un solo pannello visibile, e il conto non torna piu':
     * il primo «indietro» ne consumerebbe una e lascerebbe l'app con un
     * riavvolgimento in piu' del dovuto. Si chiude passando dall'aiutante, come
     * farebbe una riga: e' una destinazione raggiunta DA un overlay.
     */
    if (item === 'play') {
      if (strumentiAperto) {
        dalFoglioStrumenti(() => setFoglioGiocaAperto(true));
        return;
      }
      setFoglioGiocaAperto((aperto) => !aperto);
      return;
    }
    if (item === 'strumenti') {
      if (foglioGiocaAperto) {
        dalFoglioGioca(() => setStrumentiAperto(true));
        return;
      }
      setStrumentiAperto((aperto) => !aperto);
      return;
    }
    if (foglioGiocaAperto) {
      dalFoglioGioca(() => vaiAllaVoce(item));
      return;
    }
    if (strumentiAperto) {
      dalFoglioStrumenti(() => vaiAllaVoce(item));
      return;
    }
    vaiAllaVoce(item);
  };

  // Get active nav item based on screen
  const getActiveNavItem = (): NavItem => {
    /*
     * Il foglio aperto E' la voce «Gioca»: finche' sta su, la barra — che
     * resta visibile sotto di lui — lo dice con la sua casella accesa.
     *
     * Senza guardia sulla larghezza: il foglio non si smonta piu' allargando
     * la finestra. Quando montava solo a ≤768px ce ne voleva una, o la voce
     * restava accesa sul desktop per un menu che li' non esisteva.
     */
    if (foglioGiocaAperto) return 'play';
    if (strumentiAperto) return 'strumenti';
    switch (screen) {
      /*
       * `'play'`, non `'arena'` ne' `'puzzle'`.
       *
       * La regola non cambia — l'id giusto e' quello della PORTA da cui ci si
       * arriva — ma la porta si e' spostata: gare e problemi si aprono ora dal
       * foglio di «Gioca», e le voci `arena` e `puzzle` nella barra non
       * esistono piu'. Restituirle vorrebbe dire dieci schermate in cui NESSUNA
       * voce e' accesa e non si capisce da dove si e' entrati: e' lo stesso
       * guasto che questo commento descriveva quando la voce tolta era
       * `tournaments`.
       */
      case 'arena-detail':
      case 'arena-results':
      case 'arena-game':
      case 'tournaments':
      case 'tournament-detail':
      case 'tournament-lobby':
      case 'tournament-game':
      case 'puzzle':
      case 'puzzle-invite':
        return 'play';
      case 'leaderboard':
        return 'leaderboard';
      case 'faq':
        return 'faq';
      case 'rules-index':
      case 'rules-game':
      case 'rules-variant':
      case 'rules-openings':
      case 'rules-capture':
      case 'rules-tournaments':
        return 'rules';
      /* I tre attrezzi accendono la voce da cui si aprono, che e' la regola di
         sopra: l'editor non e' piu' una porta di barra, e' la prima riga di
         questo menu. */
      case 'editor':
      case 'orologio':
      case 'estrai-apertura':
        return 'strumenti';
      case 'profile':
      case 'games-history':
        return 'profile';
      default:
        return 'home';
    }
  };

  // Handle play AI from home
  const handlePlayAI = (
    selectedVariant: GameVariant,
    difficulty: AIDifficulty,
    timeControl: TimeControlPreset,
    options?: AIGameOptions,
  ) => {
    /*
     * Rete di sicurezza, non conversione: chi chiama DEVE gia' passare l'enum, e
     * PlayOverlay lo fa. Ma questa e' la confluenza di ogni percorso AI (hero,
     * modale della dashboard, CTA delle regole, foglio personalizzato) e due dei
     * montaggi qui sotto ci arrivano ancora con un `as GameVariant`, che a runtime
     * non converte niente. Su un valore gia' maiuscolo `toGameVariant` non cambia
     * nulla; su una forma da filo sfuggita evita che `createInitialBoard` esploda
     * su un config `undefined` — che e' il modo in cui il difetto si presentava:
     * un bottone che sembra non rispondere.
     */
    const variant = toGameVariant(selectedVariant);
    startLocalGame(variant, AI_MODE_MAP[difficulty] ?? 'ai-medium', timeControl, options);
    // Canonical `game-started` for AI — fired here (the single convergence point
    // for the Home overlay, the Dashboard modal, and the rules-page CTA) so every
    // entry path counts exactly once. variantProps normalizes the enum value.
    const aiLevel = AI_DIFFICULTY_ORDER.indexOf(difficulty) + 1;
    // Nessuna chiave: e' un gesto esplicito, non puo' arrivare in eco. Sul
    // perche' un suono chiamato «match-found» stia bene anche senza un
    // avversario da trovare, vedi la voce in `SOUNDS` (utils/audio.ts).
    playSound('match-found');
    trackGameStarted({ variant, timeControl, mode: 'ai', aiLevel, ruleMode: options?.ruleMode });
    trackFirstGameEver('ai');
    trackVariantBreadth(variant);
    setScreen('local-game');
  };

  // Le tabelle di apertura DENTRO l'app. Il link nell'aiuto dell'Apertura è una
  // URL vera (SEO, prerender) e finora si apriva in una scheda nuova: in PWA
  // installata vuol dire uscire dall'app. Chi lo ospita chiude prima il proprio
  // strato, così la pagina non compare sotto un foglio ancora aperto.
  const openOpeningTables = useCallback((variant: string) => {
    setRulesVariant(toGameVariant(variant));
    setScreen('rules-openings');
  }, []);

  // Partita in presenza: due persone vere a questo dispositivo, con le opzioni
  // della partita personalizzata. A differenza del tavolo pubblico non c'e' niente
  // da pubblicare e nessuno da aspettare, quindi si parte subito — stessa forma di
  // handlePlayAI, che e' l'altro avvio locale.
  const handlePlayInPerson = (settings: ChallengeSettings) => {
    startInPersonGame(settings);
    setScreen('local-game');
  };

  // Continue a solved puzzle vs the AI: seed a local game from the reached board
  // (human keeps their puzzle color, chosen difficulty, no clock) and navigate to
  // the real game screen. Closes any open puzzle overlay so it doesn't sit on top.
  const handleContinuePuzzleVsAI = (
    board: BoardState,
    humanColor: Color,
    difficulty: AIDifficulty,
    options?: { ruleMode?: RuleMode },
  ) => {
    startLocalGameFromBoard(board, AI_MODE_MAP[difficulty] ?? 'ai-medium', 'none', humanColor, options);
    const aiLevel = AI_DIFFICULTY_ORDER.indexOf(difficulty) + 1;
    playSound('match-found');
    trackGameStarted({ variant: board.variant, timeControl: 'none', mode: 'ai', aiLevel, ruleMode: options?.ruleMode ?? board.ruleMode });
    trackFirstGameEver('ai');
    trackVariantBreadth(board.variant);
    setPuzzleOverlayId(null);
    setPuzzleOverlayDemo(false);
    setPuzzleDemo(false);
    setScreen('local-game');
  };

  // Send a position from the game review to the editor. Same gate as the 'editor'
  // case of handleNavigate — registered non-guest accounts — so the button can never
  // lead to a screen that doesn't exist. The position is
  // loaded BEFORE the screen change, so EditorScreen mounts with it already there.
  const handleOpenInEditor = useCallback(async (board: BoardState) => {
    if (!isAuthenticated || isGuest) return;
    await loadEditorPosition(board);
    setScreen('editor');
  }, [isAuthenticated, isGuest]);

  /*
   * La sfida diretta, e il suo ESITO.
   *
   * Restituisce `null` se e' partita, altrimenti la CHIAVE i18n del motivo: la
   * stessa forma di `onPlayAsGuest`, perche' e' la stessa domanda («e' andata? e
   * se no, cosa dico a chi ha premuto?»).
   *
   * Prima qui c'era un `log.error` e basta, e il server ha nove modi di dire di
   * no — «quel giocatore ha appena cominciato una partita», «hai gia' una sfida
   * in attesa», «sei in un torneo in corso». Tutti e nove arrivavano e morivano
   * nella console: il foglio si chiudeva lo stesso e chi aveva premuto «Invia»
   * restava a guardare una dashboard che non diceva niente. Il codice del
   * rifiuto viaggia da `sendChallenge` (vedi la sua testata); qui si traduce.
   */
  const handleChallengePlayer = async (userId: string, settings: ChallengeSettings): Promise<string | null> => {
    log.info('App', 'Challenge sent', { userId, ...settings });
    try {
      const { challenge } = await sendChallenge(userId, settings);
      useOnlineStore.getState().setOutgoingChallenge(challenge as unknown as import('./store/online-store').Challenge);
      return null;
    } catch (e) {
      log.error('App', 'sendChallenge failed', e);
      return chiaveErroreOnlinePer(e);
    }
  };

  // Create a shareable challenge link with the settings picked in the challenge
  // modal. The overlay that follows (ChallengeLinkModal) is global, so the
  // challenger keeps it while navigating — and gets it back after a reload,
  // which matters because sending the link means leaving the app.
  const handleCreateChallengeLink = async (settings: ChallengeSettings): Promise<void> => {
    log.info('App', 'Creating challenge link', { ...settings });
    try {
      const { link } = await createLinkChallenge(settings);
      useOnlineStore.getState().setLinkChallenge(link);
      setStoredChallengeLinkId(link.id);
      trackChallengeLinkCreated(settings.variant, settings.timeControl);
    } catch (e) {
      log.error('App', 'createLinkChallenge failed', e);
    }
  };

  // Restore the challenge-link overlay after a reload (mobile browsers routinely
  // drop the tab while the user is in WhatsApp). Runs once per session, only for
  // a link that is still offerable.
  const linkRestoredRef = useRef(false);
  useEffect(() => {
    if (linkRestoredRef.current) return;
    if (!isAuthenticated || isGuest) return;
    const storedId = getStoredChallengeLinkId();
    if (!storedId) return;
    linkRestoredRef.current = true;
    void fetchChallengeLink(storedId).then((link) => {
      // La partita può essere iniziata mentre la richiesta era in volo — proprio
      // il caso WhatsApp: l'app si ricarica e la partenza differita atterra
      // appena il socket torna su. clearLinkChallenges() ha già tolto l'id
      // salvato, e quella assenza è il segnale per abbandonare il restore.
      if (getStoredChallengeLinkId() !== storedId) return;
      if (link && (link.status === 'open' || link.status === 'claimed')) {
        useOnlineStore.getState().setLinkChallenge(link);
      } else {
        clearStoredChallengeLinkId();
      }
    });
  }, [isAuthenticated, isGuest]);

  // Handle play as guest
  /**
   * Entra come ospite, e DICE se non ci è riuscito.
   *
   * Prima l'errore veniva ingoiato da un `catch` vuoto, e quel silenzio arrivava
   * fino a schermo: `loginAsGuest` scrive l'errore nello
   * store, ma nessuna schermata lo legge, e `isLoading` torna falso — quindi il
   * bottone «Gioca ora» si riattivava senza dire niente, e ripremendolo
   * ricominciava lo stesso nulla. Il limitatore lato server è 3 accessi ospite
   * ogni 15 minuti per IP: dietro un CGNAT, o sulla rete di un circolo, il quarto
   * visitatore trovava un bottone che non fa niente.
   *
   * Restituisce la CHIAVE i18n dell'errore, o `null` se è andata: chi chiama sa
   * cosa dire senza dover reinterpretare un messaggio inglese scritto per i log.
   */
  const handlePlayAsGuest = async (): Promise<string | null> => {
    try {
      await loginAsGuest();
      return null;
    } catch (err) {
      // Il 429 merita una frase sua: «riprova» non aiuta chi deve aspettare, e la
      // via d'uscita vera (accedere col proprio account) va detta.
      const status = (err as { status?: number } | null)?.status;
      return status === 429 ? 'auth.errors.guestLoginRateLimited' : 'auth.errors.guestLoginFailed';
    }
  };

  // Handle "Play this variant" from a rules page: navigate home and arm the
  // matching play modal (consumed by HomePage/DashboardPage via initialPlay).
  const handlePlayFromRules = (variant: GameVariant, mode: 'ai' | 'online') => {
    setPendingPlay({ variant, mode });
    homeScrollRef.current = 0; // fresh entry from a rules CTA → top
    setScreen('home');
  };

  // Leave-game confirmation modal state. Holds the game screen the user is
  // being asked to leave (null = closed). Shared across all 4 game screens so
  // the back-gesture guard can reuse the same modal everywhere.
  const [leaveConfirmScreen, setLeaveConfirmScreen] = useState<AppScreen | null>(null);

  /*
   * Il lobby aperto dal banner "sei in coda".
   *
   * Dashboard e Home montano gia' ognuna il PROPRIO PlayOverlay, ma solo sulla
   * schermata home: il banner invece si vede da ovunque, e da ovunque deve
   * poterlo riaprire senza portare via l'utente dalla pagina in cui si trova
   * (che puo' essere una partita). Se l'attesa e' globale, lo e' anche la
   * porta per tornarci. Le due istanze non possono aprirsi insieme: mentre
   * una e' aperta il banner e' nascosto (QueuedTableBanner.css) e l'overlay
   * copre la CTA che aprirebbe l'altra.
   */
  const [showLobbyOverlay, setShowLobbyOverlay] = useState(false);

  /*
   * Questa istanza appartiene alla schermata da cui e' stata aperta. Le due
   * locali si chiudono smontandosi al cambio schermata; questa no, e quando
   * qualcuno si siede al tavolo App passa a 'online-game' — il lobby resterebbe
   * spalancato sopra la scacchiera.
   *
   * Stessa ragione, stessa riga, per il foglio di «Gioca» e per la sfoglia che
   * apre: anche loro sono montati globalmente. Le righe li chiudono gia' da se'
   * prima di portare altrove — qui si copre il caso in cui a cambiare schermata
   * sia qualcun altro, per esempio il tavolo che trova un avversario mentre il
   * menu e' aperto.
   */
  /* Una definizione, due chiamanti: l'effetto qui sotto e la consegna della
     posizione all'editor (`useStudyHandoff`), che deve chiuderli PRIMA di
     navigare — vedi il suo primo tempo. */
  const chiudiGliOverlayGlobali = useCallback(() => {
    setShowLobbyOverlay(false);
    setFoglioGiocaAperto(false);
    setStrumentiAperto(false);
    setAvversarioSfoglia(null);
  }, []);
  useEffect(() => {
    chiudiGliOverlayGlobali();
  }, [screen, chiudiGliOverlayGlobali]);

  // --- Silent app update -------------------------------------------------
  // A freshly deployed service worker waits instead of taking over (see the
  // registerType note in vite.config.ts), so the app has to activate it itself —
  // otherwise an installed PWA that is only ever backgrounded could run a stale
  // build indefinitely. This says "a reload right now costs the user nothing".
  //
  // Being on the home screen is not enough: the auth, guest-upgrade and puzzle
  // overlays all render on top of it while `screen` stays 'home', and reloading
  // under one of those would throw away a half-typed email or a puzzle in progress.
  // useAppUpdate then adds an idle/hidden requirement, which covers the modals
  // owned by the lazy screens that App cannot see into.
  const safeToReload = isSafeToReload({
    screen,
    isAuthReady,
    mpStatus,
    arenaJoined,
    arenaStatus,
    tournamentJoined,
    tournamentStatus,
    hasPendingChallenge: Boolean(
      outgoingChallenge || incomingChallenge || incomingRematch || linkChallenge || pendingLinkChallenge
    ),
    hasOpenOverlay: Boolean(
      showAuthModal ||
        showUpgradeModal ||
        puzzleOverlayId !== null ||
        puzzleOverlayDemo ||
        showDailyPuzzles ||
        pendingPlay !== null ||
        showLobbyOverlay ||
        // Il menu di «Gioca» e la sfoglia che apre: ricaricare sotto la seconda
        // butterebbe via una partita gia' meta' configurata.
        foglioGiocaAperto ||
        strumentiAperto ||
        avversarioSfoglia !== null ||
        leaveConfirmScreen !== null
    ),
  });

  useAppUpdate(safeToReload);

  // --- Browser back-gesture / back-button integration --------------------
  // The app is a state-based router with no History API integration, so any
  // back gesture would exit the PWA. useBackNavigation maps the browser history
  // onto the logical location below and traps back while a game is in progress.
  const location = useMemo<AppLocation>(
    () => ({
      screen,
      profileUsername,
      profileTab,
      faqEntry,
      tournamentAnchor,
      selectedArenaId,
      selectedTournamentId,
      selectedPuzzleId,
      rulesVariant,
      rulesIndex: screen === 'rules-index',
      replayGameId,
    }),
    [screen, profileUsername, profileTab, faqEntry, tournamentAnchor, selectedArenaId, selectedTournamentId, selectedPuzzleId, rulesVariant, replayGameId]
  );

  const applyLocation = useCallback((loc: AppLocation) => {
    /*
     * UNA VOCE CHE NON E' PIU' UN POSTO NON SI RIPRISTINA.
     *
     * La lobby di un torneo (e la sua damiera) restano in cronologia per tutto
     * il tempo, ma a torneo concluso non sono pagine: l'indietro ci rimontava la
     * sala d'attesa di un torneo finito — col suo polling e il suo ingresso
     * nella stanza socket — e se lo stato era ancora 'finished'
     * l'instradamento la rimbalzava sulla scheda mangiandosi le voci in avanti.
     * Si atterra direttamente dove quel rimbalzo avrebbe portato.
     *
     * Non costa nessuna voce: il render del pop consuma `isPoppingRef`
     * (useBackNavigation.ts:187-190), quindi l'osservatore non spinge niente e
     * lo stack scende di una voce a ogni pressione.
     *
     * Con `getState()` e non dai valori di render, come `isGameInProgress`:
     * questa la chiama il gestore `popstate`, e un valore di render sarebbe
     * vecchio.
     *
     * Due conseguenze note e inerti, nello stesso stampo delle due rimappature
     * difensive qui sotto: la cima dello specchio di `useBackNavigation`
     * continua a dire `tournament-lobby` mentre a schermo c'e' la scheda, e
     * l'indirizzo di quella voce resta `/` invece del percorso della gara.
     */
    const torneo = useTournamentStore.getState();
    const rimappata = doveAtterraLaVoceDelTorneo(loc.screen, torneo.status);

    // Set params before the screen so guarded render branches
    // (e.g. `screen === 'profile' && profileUsername`) don't fall through.
    setProfileUsername(loc.profileUsername);
    setProfileTab(loc.profileTab);
    setFaqEntry(loc.faqEntry);
    setTournamentAnchor(loc.tournamentAnchor as TournamentRulesAnchor | null);
    setSelectedArenaId(loc.selectedArenaId);
    /* Rimappando serve un id a tutti i costi: il ramo che disegna la scheda e'
       guardato da `selectedTournamentId`, e senza cadrebbe sul ripiego della
       home. La VOCE prima dello store — sa di quale torneo era quella lobby,
       mentre lo store sa solo qual e' l'ultimo caricato. */
    setSelectedTournamentId(
      rimappata
        ? (loc.selectedTournamentId ?? torneo.currentTournament?.id ?? null)
        : loc.selectedTournamentId,
    );
    setSelectedPuzzleId(loc.selectedPuzzleId);
    setRulesVariant((loc.rulesVariant as GameVariant | null) ?? null);
    // Senza questa, tornare sulla posizione 'games-history' rimontava lo Storico
    // a mani vuote — l'elenco, non la review che l'utente ci aveva aperto.
    setReplayGameId(loc.replayGameId);
    /*
     * Difensivo, e sono due casi ormai.
     *
     * `'landing'` e' il vecchio bersaglio di reindirizzamento. `'arena'` e' una
     * schermata che non esiste piu': le arene sono righe dell'elenco dei
     * tornei. Una voce di cronologia scritta PRIMA di questo rilascio la porta
     * ancora, e senza questa riga cadrebbe fuori da ogni ramo di render — una
     * pagina vuota al primo «indietro» di chi aveva l'app aperta durante il
     * deploy.
     */
    const schermata = rimappata ?? (
      loc.screen === 'landing' ? 'home'
      : loc.screen === 'arena' ? 'tournaments'
      : loc.screen);
    setScreen(schermata as AppScreen);
  }, []);

  // Map content locations to their real, localized URL path; every screen that
  // has no URL of its own (a game, the profile, the leaderboard) resolves to the
  // landing, which is where the SPA actually lives.
  const locationToUrl = useCallback((loc: AppLocation): string | undefined => {
    /*
     * UNA GARA HA UN INDIRIZZO SUO.
     *
     * Prima non ce l'aveva: `cleanUrl()` azzerava la query all'arrivo e nessuno
     * la riscriveva, quindi su un torneo la barra diceva sempre `/` — copiarla
     * condivideva la home. Chi voleva mandare in giro una classifica non aveva
     * ne' un bottone (a torneo finito non c'era) ne' un indirizzo.
     *
     * Non serve cronologia nuova: l'osservatore di `useBackNavigation` scrive
     * GIA' `urlFor(location)` a ogni navigazione in avanti, e `sameLocation`
     * confronta GIA' l'identificativo della gara. Qui basta restituire il
     * percorso giusto.
     *
     * `percorsoGara` e non una stringa scritta a mano: e' la stessa funzione da
     * cui esce il link che si manda in chat. Due forme scritte in due posti,
     * il giorno che una cambiasse l'altra resterebbe indietro in silenzio.
     *
     * Mai il gettone: questo finisce nella barra degli indirizzi, e la chiave
     * d'ingresso di una gara privata non ci va.
     */
    if (loc.screen === 'tournament-detail' && loc.selectedTournamentId) {
      return percorsoGara('tournament', loc.selectedTournamentId);
    }
    /* `arena-results` e' il podio: lo vede solo chi ha partecipato, quindi un
       ricarico atterra sul dettaglio e l'effetto di instradamento piu' sotto lo
       rimette sul podio — stesso stato, e con `navigateReplace` nessuna voce in
       piu'. */
    if ((loc.screen === 'arena-detail' || loc.screen === 'arena-results') && loc.selectedArenaId) {
      return percorsoGara('arena', loc.selectedArenaId);
    }
    const lang: RulesLang = isRulesLang(i18n.language) ? i18n.language : 'en';
    return contentUrlFor(lang, loc.screen as AppScreen, (loc.rulesVariant as GameVariant | null) ?? null);
  }, [i18n.language]);

  /* La regola sta in `laPartitaDellaSchermataEInCorso` — con l'arena e il suo
     'gameEnd' — perche' la condividiamo con la review, che la stessa domanda la
     deve fare in modo REATTIVO. Qui si legge con `getState()`: questa la chiama
     il gestore `popstate`, e un valore di render sarebbe vecchio. */
  const isGameInProgress = useCallback((gameScreen: string): boolean => (
    laPartitaDellaSchermataEInCorso(gameScreen, {
      locale: useGameStore.getState().status,
      online: useMultiplayerStore.getState().status,
      torneo: useTournamentStore.getState().status,
      arena: useArenaStore.getState().status,
    })
  ), []);

  const onGuardedBack = useCallback((gameScreen: string) => {
    setLeaveConfirmScreen(gameScreen as AppScreen);
  }, []);

  const { navigateReplace, tornaIndietro } = useBackNavigation({ location, applyLocation, isGameInProgress, onGuardedBack, locationToUrl });

  /*
   * «Studia in editor», il lato di App: pubblica alla review se la schermata
   * sotto ha una partita viva, e porta all'editor le posizioni che la review
   * deposita. Prop non ce ne sono — il bottone compariva solo nei due ospiti
   * della review che la passavano, e mancava in torneo, arena e profilo in
   * overlay. Vedi `store/study-store.ts`.
   *
   * Sta QUI, dopo `useBackNavigation`, per la stessa ragione dell'instradamento
   * dell'arena: naviga, e per farlo bene deve girare dopo l'osservatore della
   * cronologia.
   */
  useStudyHandoff(
    laPartitaDellaSchermataEInCorso(screen, {
      locale: localGameStatus,
      online: mpStatus,
      torneo: tournamentStatus,
      arena: arenaStatus,
    }),
    chiudiGliOverlayGlobali,
    handleOpenInEditor,
  );

  /* Il pinch a molla: ingrandisce mentre tieni le dita, torna alla vista di
     partenza al rilascio. Sta qui accanto a `useBackNavigation` perche' e' della
     stessa specie — un gestore di gesti globale che l'app deve avere in UNA
     copia sola. Sulle schermate con la scacchiera a tutto schermo non fa nulla:
     lo decidono loro, chiamando `useBloccoLente`. */
  useLente();
  useTorneoDopoLaRiconnessione();

  /*
   * Instradamento della sezione arena. Sta QUI, dopo `useBackNavigation`, e non
   * piu' su in mezzo agli altri effetti: e' l'unico effetto che NAVIGA in base
   * allo stato, e per farlo bene deve girare dopo l'osservatore della
   * cronologia — la stessa ragione per cui ci sta il collegamento profondo del
   * puzzle, qui sotto. Messo prima, il suo `navigateReplace()` veniva consumato
   * dall'osservatore della schermata SBAGLIATA — quella da cui si stava
   * partendo — e al posto della voce del dettaglio si mangiava quella
   * dell'elenco: due pressioni dell'indietro e ci si ritrovava sulla Home.
   * Misurato, non dedotto.
   */
  useEffect(() => {
    if ((arenaStatus === 'matchFound' || arenaStatus === 'playing') && screen !== 'arena-game') {
      /*
       * Si puo' arrivare qui DALLO SPETTATORE: chi guarda resta in coda, e
       * l'accoppiamento lo porta sulla propria partita. Il bersaglio va
       * dimenticato uscendo, altrimenti resta una voce di cronologia
       * 'spectate' che sa ancora chi guardare, e l'indietro a partita finita
       * riapriva lo spettatore su una partita di mesi prima invece che sulla
       * classifica. `spectateTargetUserId` non sta in `AppLocation` proprio
       * perche' una voce restaurata non debba avere un bersaglio (vedi la sua
       * dichiarazione, `App.tsx:294-301`): qui si rende vero di nuovo.
       */
      if (screen === 'spectate') setSpectateTargetUserId(null);
      setScreen('arena-game');
    } else if (
      (arenaStatus === 'searching' || arenaStatus === 'lobby' || arenaStatus === 'finished') &&
      screen === 'arena-game'
    ) {
      /*
       * 'finished' non era contemplato: quando l'arena chiudeva restavamo sulla
       * schermata di gioco con la damiera congelata e nessuna via d'uscita.
       *
       * Il podio pero' spetta SOLO a quel caso. Gli altri due stati che escono
       * dalla damiera sono la vita normale di un'arena viva, e sono i due
       * bottoni del riepilogo di fine partita: «Continua» (`resumeSearch()`,
       * 'searching') e «In pausa» (`pauseSearch()`, 'lobby'). Mandando li'
       * chiunque fosse iscritto, a ogni partita finita senza un accoppiamento
       * gia' pronto si apriva il podio — trofeo, badge «terminata» — di
       * un'arena ancora in corso, con il giocatore in coda.
       *
       * E la voce di cronologia della damiera si SOSTITUISCE, non si aggiunge,
       * per la stessa ragione della sostituzione qui sotto: `isGameInProgress`
       * (`App.tsx:923-941`) non trattiene piu' nulla a partita finita, quindi
       * l'indietro tornava sulla damiera e questo stesso effetto rispediva
       * subito fuori — un rimbalzo senza fine, da cui si usciva solo coi
       * bottoni dentro la pagina.
       */
      navigateReplace();
      setScreen(arenaStatus === 'finished' && arenaJoined ? 'arena-results' : 'arena-detail');
    } else if (arenaStatus === 'finished' && arenaJoined && screen === 'arena-detail') {
      /*
       * L'arena finisce mentre la guardi: chi ci ha giocato merita il podio e
       * il proprio piazzamento, non la stessa classifica di un minuto prima.
       * `ArenaFinalResultsPage` esisteva da mesi senza che nessuno la aprisse.
       */
      /*
       * SOSTITUISCE la voce di cronologia del dettaglio invece di aggiungerne
       * una, come fa `leaveGameTo` uscendo da una partita
       * (`useBackNavigation.ts:44-49`). Senza, il gesto indietro era una
       * TRAPPOLA e non un'uscita: si tornava al dettaglio, questo ramo
       * rimandava al podio e l'osservatore della cronologia
       * (`useBackNavigation.ts:157-182`) rimetteva la voce appena consumata —
       * lo stack tornava identico a ogni pressione, e dal podio non si usciva
       * piu'. Regressione di questo stesso blocco: prima l'azzeramento allo
       * smontaggio del dettaglio spegneva 'finished' e l'indietro usciva.
       *
       * Per un'arena conclusa a cui hai giocato il dettaglio non e' una pagina,
       * e' un rimbalzo: sostituirne la voce e' dire alla cronologia esattamente
       * questo. L'indietro dal podio riporta quindi all'elenco, in una
       * pressione.
       */
      navigateReplace();
      setScreen('arena-results');
    }
  }, [arenaStatus, screen, arenaJoined, navigateReplace]);

  // ── Shared daily-puzzle deep link: resolve it once auth has settled ─────────
  //
  // Declared after useBackNavigation so the back stack is already seeded before
  // this can navigate. MUST gate on isAuthReady, never on isAuthenticated alone:
  // isAuthenticated is persisted while accessToken is not, so at mount a
  // returning registered user reads "authenticated" with no token — resolving
  // there would show them the "create an account" gate over their own account.
  //
  // The daily puzzle is a REGISTERED-ONLY feature (it carries a rating and a
  // Streak; guests get the client-side demo puzzle instead). This link used to
  // be the one hole in that rule: it minted a guest session for its recipient
  // and handed over the real puzzle. Now a non-registered recipient gets the
  // invite screen, and the link is remembered so authenticating resumes into it.
  const openPuzzleRef = useRef(openPuzzle);
  openPuzzleRef.current = openPuzzle;

  useEffect(() => {
    if (!dailyDeepLinkVariant || dailyDeepLinkDone.current) return;
    if (!isAuthReady) return; // checkAuth()/refresh still in flight.

    const { isAuthenticated: authed, isGuest: guest } = useAuthStore.getState();
    if (!authed || guest) {
      // Remember the link so it survives a registration's email round trip (see
      // utils/pending-puzzle-link), then gate. Deliberately does NOT set
      // dailyDeepLinkDone: this effect has to run again when they authenticate,
      // which is what resumes them into the puzzle. The branch is idempotent, so
      // StrictMode's double mount is harmless here.
      setPendingPuzzleLink(dailyDeepLinkVariant);
      // Only a link carried by the URL takes over the screen. A link restored
      // from storage stays silent — otherwise it would hijack /verify?token=…,
      // the very journey it exists to support.
      setDailyDeepLinkState(dailyDeepLinkFromUrl ? 'gate' : 'idle');
      return;
    }

    // Resolve at most once per attempt (the retry button clears this). This ref
    // is also what makes StrictMode's mount → unmount → mount safe: the second
    // invocation bails instead of opening the puzzle twice. Deliberately no
    // cleanup-driven "cancelled" flag — under StrictMode it would throw away
    // the result of the first, still-valid run and nothing would ever open.
    dailyDeepLinkDone.current = true;

    void (async () => {
      // Fetched with the session in place so `solved` reflects this user.
      const res = await fetchDailyPuzzle(dailyDeepLinkVariant);
      if (!res?.puzzle) {
        setDailyDeepLinkState('error-link');
        return;
      }
      clearPendingPuzzleLink();
      setDailyDeepLinkState('idle');
      // Step off the invite screen before opening. On phones openPuzzle only
      // raises the overlay sheet and leaves `screen` alone, which would leave
      // the gate rendered underneath it. On desktop openPuzzle sets 'puzzle'
      // right after, in the same batch, so this is invisible there.
      setScreen((s) => (s === 'puzzle-invite' ? 'home' : s));
      // Read through a ref: openPuzzle is re-created on every render.
      openPuzzleRef.current(res.puzzle.id, false);
    })();
    // isAuthenticated/isGuest ARE the resume: login() and upgradeGuest() flip
    // them in the store, this effect re-runs with dailyDeepLinkDone still false,
    // and the puzzle opens. No AuthModal success callback needed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dailyDeepLinkVariant, isAuthReady, isAuthenticated, isGuest, deepLinkAttempt]);

  /*
   * Chi arriva da un invito e ha GIA' un conto non deve registrarsene un altro:
   * accede, e si ritrova nel torneo. Il login e' un pedaggio, non una deviazione.
   *
   * Chi un conto non ce l'ha si registra, e il ritorno lo fa l'altro capo del
   * viaggio — `VerifyEmailPage`, quando il link della posta chiude il giro.
   *
   * La regola su CHI vada riportato sta in `decidiRitornoAlTorneo`, con le sue
   * caselle: e' l'unica parte di questo giro che si sbaglia in silenzio.
   * `eraFuoriRef` e' quello che distingue «ha appena fatto il login» da «apre
   * l'app con la sessione gia' aperta»: il secondo non si dirotta.
   */
  const eraFuoriRef = useRef(false);
  useEffect(() => {
    if (!isAuthReady) return; // checkAuth()/rinnovo ancora in volo.
    if (!isAuthenticated || isGuest) eraFuoriRef.current = true;

    const invito = leggiInvitoInSospeso();
    const esito = decidiRitornoAlTorneo({
      autenticato: isAuthenticated,
      ospite: isGuest,
      eraFuori: eraFuoriRef.current,
      invito,
      giaSulTorneo: screen === 'tournament-detail',
    });
    if (esito === 'aspetta') return;

    // Speso in tutti e due i casi: o si e' arrivati, o non c'era niente da
    // riprendere. Un ricordo che resta dirotta il giro dopo.
    dimenticaInvitoInSospeso();
    if (esito === 'torna' && invito) {
      log.info('App', 'Invito in sospeso: ritorno al torneo dopo l\'accesso', { id: invito.id });
      setSelectedTournamentId(invito.id);
      setInviteTournamentToken(invito.token);
      setInviteTournamentId(invito.id);
      setScreen('tournament-detail');
    }
  }, [isAuthReady, isAuthenticated, isGuest, screen]);

  /*
   * Shared position link: /?editor=<fen>&variant=… → the editor, with that position
   * already on the table.
   *
   * The editor needs a real account (same gate as the nav item, see handleNavigate),
   * so a logged-out recipient is shown the login modal and the link waits in memory
   * for the session. Accepted limitation: unlike the daily-puzzle link it is NOT
   * persisted, so it does not survive a registration's email round trip — the
   * verification link typically opens in a new browsing context, and the visitor
   * re-taps the shared link. Persisting it would be pending-puzzle-link's job, and
   * that helper is puzzle-shaped (it expires at UTC midnight).
   */
  useEffect(() => {
    if (!editorDeepLink || editorDeepLinkDone.current) return;
    if (!isAuthReady) return; // checkAuth()/refresh still in flight.

    const { isAuthenticated: authed, isGuest: guest } = useAuthStore.getState();
    if (!authed || guest) {
      // Deliberately does NOT set editorDeepLinkDone: this effect has to run again
      // once they authenticate, which is what opens the position. Idempotent, so
      // StrictMode's double mount is harmless.
      apriAccesso();
      return;
    }

    // Resolve at most once — and the position goes in BEFORE the screen change,
    // because the editor store is a module singleton and EditorScreen has to mount
    // with it already there (same order as handleOpenInEditor).
    editorDeepLinkDone.current = true;
    void (async () => {
      const ok = await loadEditorFen(editorDeepLink.fen, toGameVariant(editorDeepLink.variant));
      // An unreadable FEN opens nothing: the visitor stays on the home page rather
      // than landing in an editor showing something that isn't what was shared.
      if (ok) setScreen('editor');
    })();
    // isAuthenticated/isGuest ARE the resume: login() flips them in the store and
    // this effect re-runs with editorDeepLinkDone still false.
  }, [editorDeepLink, isAuthReady, isAuthenticated, isGuest]);

  // Leave the invite screen for good: the visitor declined, so drop the stored
  // link (a later visit must not be dragged back here) and replace the history
  // entry so a back press can't return to a gate whose link is gone.
  const leaveDailyPuzzleGate = useCallback(() => {
    clearPendingPuzzleLink();
    setDailyDeepLinkState('idle');
    navigateReplace();
    homeScrollRef.current = 0;
    setScreen('home');
  }, [navigateReplace]);

  // Leave a game: replace the game's history entry with the destination so a
  // later back press doesn't return to the abandoned game screen.
  const leaveGameTo = useCallback((dest: AppScreen) => {
    // Leaving a game to home is a fresh entry → land at the top.
    if (dest === 'home') homeScrollRef.current = 0;
    navigateReplace();
    setScreen(dest);
  }, [navigateReplace]);

  /*
   * …e la strada opposta, che mancava. Quando il rientro non riesce, lo store
   * RILASCIA la partita (stanza e damiera a nullo, stato 'connected'): senza
   * questa uscita il giocatore resta qui, su una damiera che non e' piu' di
   * nessuna partita, dove le mosse non arrivano da nessuna parte, non c'e' fine
   * partita, non c'e' rivincita e non c'e' nemmeno un bottone per andarsene.
   * Rilasciare invece di aggrapparsi ha senso solo se porta fuori davvero.
   *
   * La condizione sta in `laPartitaOnlineEStataRilasciata` — con i suoi casi da
   * NON toccare, a cominciare dalla pausa fra le due manche — cosi' e' provabile
   * senza montare mezza applicazione.
   */
  useEffect(() => {
    if (laPartitaOnlineEStataRilasciata({
      screen, status: mpStatus, roomId: mpRoomId, haUnaDamiera: mpHaUnaDamiera,
    })) {
      /*
       * «Fuori» non e' lo stesso posto per tutti. Chi e' iscritto a un torneo
       * che sta girando deve tornare dove l'accoppiamento del turno successivo
       * lo raggiunge: alla home resterebbe a guardare una schermata che non
       * riceve nulla, mentre altrove l'orologio della sua partita nuova e' gia'
       * partito.
       */
      const destinazione = doveTornareDopoIlRilascio(
        tournamentJoined && tournamentStatus !== 'browsing' && tournamentStatus !== 'viewing',
      );
      log.warn('App', 'partita online rilasciata dal rientro', { destinazione });
      leaveGameTo(destinazione);
    }
  }, [screen, mpStatus, mpRoomId, mpHaUnaDamiera, leaveGameTo, tournamentJoined, tournamentStatus]);

  /*
   * La stessa uscita per la partita di TORNEO, che e' la seconda meta' di G6
   * (`docs/AUDIT-TORNEI-DATI-2026-09-24.md`) e l'ultimo dei tre casi: l'online
   * ce l'ha qui sopra, l'arena dentro il proprio componente
   * (`ArenaGame`, `status === 'lobby' && !boardState`), il torneo non ce
   * l'aveva.
   *
   * Succede a ogni deploy con una partita di torneo in corso: le stanze vivono
   * in RAM, il riavvio le porta via, il rientro riceve un «no» autorevole e
   * `rilasciaPartita` svuota lo store. Da li' nessun ramo di questa navigazione
   * portava via — e la damiera restava DISEGNATA (vedi la nota del predicato),
   * quindi si fissava una posizione che sembra una partita, con i bottoni muti.
   *
   * Va dopo l'effetto online, non prima: sono mutuamente esclusivi (guardano
   * due schermate diverse), e tenerli nell'ordine in cui si leggono rende
   * evidente che sono lo stesso gesto fatto due volte.
   */
  useEffect(() => {
    if (!laPartitaDiTorneoEStataRilasciata({
      screen, currentRoomId: torneoRoomId, haUnaDamiera: torneoHaUnaDamiera,
      gameResult: tournamentGameResult,
    })) return;
    /*
     * A torneo FINITO non ci si mette in mezzo: quel caso ha gia' il suo ramo
     * (l'effetto della navigazione dei tornei, qui sopra), che porta alla
     * SCHEDA — li' il vincitore lo legge anche chi alla fine non c'era. Senza
     * questa riga i due effetti girerebbero sullo stesso render e vincerebbe
     * questo, dirottando in lobby una decisione presa altrove.
     */
    if (tournamentStatus === 'finished') return;
    /*
     * L'id PRIMA della schermata, come fa `onGameEnd`: il ramo del dettaglio e'
     * guardato da `selectedTournamentId`, e dalla lobby il tasto indietro va
     * proprio li' — senza, cadrebbe sul ripiego della home.
     */
    const idTorneo = useTournamentStore.getState().currentTournament?.id;
    if (idTorneo) setSelectedTournamentId(idTorneo);
    const destinazione = doveTornareDopoIlRilascio(
      tournamentJoined && tournamentStatus !== 'browsing' && tournamentStatus !== 'viewing',
    );
    log.warn('App', 'partita di torneo rilasciata dal rientro', { destinazione });
    /*
     * `leaveGameTo` e non `setScreen`, ed e' la differenza fra uscire e
     * sembrare di uscire: sostituisce la voce di cronologia, quindi il tasto
     * indietro non riporta sulla damiera morta. Quell'uscita a mano — l'unica
     * che c'era — la lasciava invece in cronologia, e nemmeno chiedeva conferma
     * (`laPartitaDellaSchermataEInCorso` guarda lo stato 'playing', che il
     * rilascio ha gia' cambiato).
     */
    leaveGameTo(destinazione);
  }, [screen, torneoRoomId, torneoHaUnaDamiera, tournamentGameResult,
      tournamentJoined, tournamentStatus, leaveGameTo]);

  /**
   * Enter the spectator screen once the challenge modal has actually unmounted.
   *
   * The modal registers with useOverlayBackButton, which pushes a "buffer"
   * history entry and pops it with history.back() on close. Navigating in the
   * same tick would race that pop and strand a ghost entry, so the back gesture
   * from the spectator screen would need two presses to reach home. Deferring
   * by one effect tick lets the pop land first.
   */
  useEffect(() => {
    if (!pendingSpectateUserId) return;
    setSpectateTargetUserId(pendingSpectateUserId);
    setPendingSpectateUserId(null);
    setScreen('spectate');
  }, [pendingSpectateUserId]);

  const handleSpectatePlayer = useCallback((userId: string) => {
    /*
     * La porta di sempre, e l'azzeramento e' obbligatorio, non pulizia: dallo
     * spettatore si esce anche SENZA passare da `handleLeaveSpectate`, cioe'
     * accettando una sfida mentre si guarda (l'instradamento di
     * `mpStatus === 'playing'`, qui sopra, per cui 'spectate' e' fuori da
     * `activeGameScreens`). Per quella strada il ritorno all'arena resta
     * armato, e la visita dopo — entrata dalla lista online — finirebbe in
     * un'arena da cui non e' mai passata.
     */
    setSpectateReturn(null);
    setPendingSpectateUserId(userId);
  }, []);

  /*
   * La porta dell'arena. Callback separata invece di un secondo parametro su
   * `handleSpectatePlayer`: quella la ricevono cinque schermate diverse, e un
   * parametro facoltativo si perde in silenzio in mezzo a un `props` girato.
   */
  const handleSpectateFromArena = useCallback((userId: string) => {
    setSpectateReturn({ screen: 'arena-detail', arenaId: selectedArenaId });
    setPendingSpectateUserId(userId);
  }, [selectedArenaId]);

  /* La stessa porta, dal torneo. Vale per entrambi i formati a turni: chi
     guarda non sta giocando, e il torneo continua senza di lui. */
  const handleSpectateFromTournament = useCallback((userId: string) => {
    setSpectateReturn({ screen: 'tournament-detail', arenaId: null, tournamentId: selectedTournamentId });
    setPendingSpectateUserId(userId);
  }, [selectedTournamentId]);

  const handleLeaveSpectate = useCallback(() => {
    setSpectateTargetUserId(null);
    const ritorno = spectateReturn;
    setSpectateReturn(null);
    /*
     * L'arena si rimette PRIMA della schermata, come fa `applyLocation`: il
     * ramo che disegna il dettaglio e' guardato da `selectedArenaId`, e senza
     * l'arena la schermata da sola cade sul ripiego della home — che e' dove si
     * finiva prima, cioe' il difetto che questa uscita chiude.
     */
    if (ritorno?.screen === 'arena-detail' && ritorno.arenaId) {
      setSelectedArenaId(ritorno.arenaId);
      leaveGameTo('arena-detail');
      return;
    }
    /* Stessa cautela per il torneo: prima l'id, poi la schermata. */
    if (ritorno?.screen === 'tournament-detail' && ritorno.tournamentId) {
      setSelectedTournamentId(ritorno.tournamentId);
      leaveGameTo('tournament-detail');
      return;
    }
    leaveGameTo('home');
  }, [spectateReturn, leaveGameTo]);

  const handleLeaveLocalGame = useCallback(() => {
    const gameStatus = useGameStore.getState().status;
    if (gameStatus === GameStatus.PLAYING) {
      setLeaveConfirmScreen('local-game');
      return;
    }
    leaveGameTo('home');
  }, [leaveGameTo]);

  // Confirm leaving the game — resign on the server, tear down store state
  // (status off 'playing' so the auto-nav effects don't pull us back), then
  // navigate to the correct destination.
  const confirmLeave = useCallback(() => {
    switch (leaveConfirmScreen) {
      case 'tournament-game':
        useTournamentStore.getState().resign();          // emette game:resign (conta come sconfitta)
        useTournamentStore.getState().tornaAllaLobby();  // solo lo stato locale, mantiene currentTournament
        leaveGameTo('tournament-lobby');
        break;
      case 'arena-game':
        useArenaStore.getState().resign();            // emette game:resign
        useArenaStore.getState().pauseSearch();        // status -> 'lobby', mantiene currentArena
        leaveGameTo('arena-detail');
        break;
      case 'online-game':
        useMultiplayerStore.getState().resign();       // emette game:resign
        useMultiplayerStore.getState().reset();        // emette game:leave, status -> 'connected'
        leaveGameTo('home');
        break;
      case 'local-game':
      default: {
        // Stesso esito del comando nella barra, o uscire e terminare direbbero
        // due cose diverse della stessa partita: in presenza non c'e' nessuno a
        // cui consegnarla, contro il computer l'umano perde.
        const partitaLocale = useGameStore.getState();
        if (isHotseatMode(partitaLocale.gameMode)) {
          partitaLocale.abandonGame();                 // status terminale sincrono (no socket)
        } else {
          partitaLocale.resign();
        }
        leaveGameTo('home');
        break;
      }
    }
    setLeaveConfirmScreen(null);
  }, [leaveConfirmScreen, leaveGameTo]);

  const cancelLeave = useCallback(() => {
    setLeaveConfirmScreen(null);
  }, []);

  // Close leave-confirm modal on Escape key
  useEffect(() => {
    if (!leaveConfirmScreen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLeaveConfirmScreen(null);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [leaveConfirmScreen]);

  // Logout handler
  const handleLogout = () => {
    log.info('App', 'Logout');
    trackLogout();
    mpDisconnect();
    // FIX-44: Reset all stores on logout to avoid stale state
    useGameStore.getState().resetGame();
    useTournamentStore.getState().reset();
    useArenaStore.getState().resetAlLogout();
    /*
     * Anche la lista online e la lobby: i loro `reset()` esistevano ed erano MAI
     * CHIAMATI (grep su tutto `src/`, esclusi i test: zero occorrenze).
     *
     * Cosa sopravviveva a un logout su un dispositivo condiviso: `onlineUsers` e
     * `counts`, `linkChallenge` e `pendingLinkChallenge` — che
     * alimentano `GlobalChallengeLinkOverlay` — e `myTableId`/`isBusy` di
     * `useLobbyStore`, che alimentano `QueuedTableBanner`. Il nuovo utente eredita
     * quindi il tavolo in attesa e il link di sfida del precedente, e li vede come
     * propri. (I due banner degli inviti si spengono da sé alla scadenza
     * dell'invito, quindi il danno vero è questo, non quelli.)
     */
    useOnlineStore.getState().reset();
    useLobbyStore.getState().reset();
    clearDashboardCache(); // drop the previous account's cached dashboard summary
    // A shared device must not resurrect the previous user's puzzle link.
    clearPendingPuzzleLink();
    // Ne' l'invito a un torneo di chi c'era prima.
    /* Anche il gettone in memoria, non solo quello su disco: su un dispositivo
       condiviso il conto successivo che apre lo stesso torneo si ritroverebbe
       applicato l'invito di chi c'era prima. */
    scartaGettoneInvito();
    logout();
    homeScrollRef.current = 0; // fresh session → top of home
    setScreen('home');
  };

  // Determine if sidebar should be shown
  const shouldShowSidebar = !['local-game', 'online-game', 'tournament-game', 'arena-game', 'spectate'].includes(screen);

  // Hydrate user preferences from backend after login (registered users only)
  const hydratePreferences = usePreferencesStore((state) => state.hydrateFromServer);
  useEffect(() => {
    // Only after checkAuth/refresh has established the session (token ready), not on the
    // persisted isAuthenticated flag alone — otherwise preferences fetch 401s at boot.
    if (isAuthReady && isAuthenticated && !isGuest) {
      log.info('App', 'Hydrating preferences after auth');
      hydratePreferences();
    }
  }, [isAuthReady, isAuthenticated, isGuest, hydratePreferences]);

  // Dentro l'app installata: dirlo al server, cosi' la scheda del browser dello
  // stesso account smette di proporre l'installazione. Serve a iOS, dove i due
  // storage non si parlano — vedi l'hook.
  useReportPwaInstall(isAuthReady, isAuthenticated, isGuest);

  // Tornati sulla home dopo un problema aperto dalla lista: la lista si riapre.
  // Al tick dopo, non nello stesso: il ritorno passa da un `popstate`, e
  // useBackNavigation rimette a posto lo stato della cronologia subito dopo —
  // riaprendo qui e ora, la voce «cuscinetto» dell'overlay verrebbe sovrascritta
  // e il primo «indietro» uscirebbe dall'app invece di chiudere la lista.
  useEffect(() => {
    if (!reopenDailyPuzzles) return;
    // Il problema e' ancora aperto: si aspetta.
    if (screen === 'puzzle') return;
    // Uscito da tutt'altra parte (Arena, profilo…): la lista non deve
    // ricomparire a sorpresa al prossimo passaggio dalla home.
    if (screen !== 'home') { setReopenDailyPuzzles(false); return; }
    const id = setTimeout(() => {
      setReopenDailyPuzzles(false);
      setShowDailyPuzzles(true);
    }, 0);
    return () => clearTimeout(id);
  }, [screen, reopenDailyPuzzles]);

  // Cookie consent banner (renders on all screens)
  const cookieBanner = <><BoardThemeApplier /><CookieBanner /></>;

  /*
   * L'avviso del rientro tardivo, e sta sulla home perche' e' li' che si
   * finisce: il rilascio della partita e' seguito da `leaveGameTo('home')`
   * nell'effetto qui sopra, quindi questa e' l'unica schermata in cui possa
   * comparire.
   */
  const avvisoRientro = (
    <AvvisoRientro
      visibile={partitaTerminataMentreEriVia}
      onChiudi={clearPartitaTerminataMentreEriVia}
    />
  );

  // Global challenge overlays (rendered on all screens). Incoming direct
  // challenges surface through a single prominent banner everywhere, so the
  // invite is impossible to miss regardless of the current page.
  const challengeOverlays = (
    <>
      {/* Mounted with the other global overlays because a shared link can land on
          any of four screens (invite, replay, editor, puzzle gate) — this slot is
          already on all of them. */}
      <OpenInAppBanner arrivalUrl={arrivalUrl} active={arrivedFromShare} />
      {/*
        * Il modale di ACCESSO, uno solo per tutta l'app.
        *
        * Erano ventitre', uno per schermata, e diciassette di quelli omettevano
        * `initialMode`: lo stato del modo esisteva e nessuno lo leggeva. Finche'
        * il modo voluto era sempre «accedi» l'omissione non si vedeva (il default
        * copre), ma bastava volerne aprire un altro perche' diciassette schermate
        * mostrassero la cosa sbagliata. Qui e' letto in un posto, e `apriAccesso`
        * e' l'unico che lo scrive.
        *
        * `challengeOverlays` sta su trenta schermate, sei piu' di quelle che
        * montavano il modale: quei sei «Accedi» nella barra non avevano niente da
        * aprire.
        */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => { setShowAuthModal(false); }}
        initialMode={authModalInitialMode}
      />
      {/* La modale di CONVERSIONE ospite→registrato, globale come quella qui
          sopra. Ci arriva chi vuole un conto TENENDO quello che ha fatto da
          ospite — `GuestBanner`, il fondo dei puzzle, l'overlay delle porte
          riservate — perche' `api.register` creerebbe un utente nuovo buttando
          via le PARTITE che ha giocato (e con loro la streak di attivita', che
          da quelle deriva). NON ci arriva chi preme «Accedi»: quello e' rientro
          nel proprio conto, e dirottarlo qui lo murava fuori. */}
      <AuthModal
        isOpen={showUpgradeModal}
        onClose={() => { setShowUpgradeModal(false); }}
        initialMode="upgrade"
      />
      <GlobalIncomingChallengeBanner />
      <GlobalIncomingRematchBanner />
      <GlobalOutgoingChallengeOverlay />
      <GlobalChallengeLinkOverlay />
      {/* A lobby table outlives the overlay that opened it, so the wait has to
          stay visible — and cancellable — from every screen. E toccabile: la riga
          riapre il lobby qui sotto, senza cambiare schermata. */}
      <QueuedTableBanner onOpenLobby={() => setShowLobbyOverlay(true)} />
      {/* E lo stesso per una GARA in corso: il server accoppia chi e' connesso,
          non chi sta guardando la pagina del torneo, quindi l'attesa del turno
          va letta da ovunque — e da ovunque si deve poter tornare indietro.
          Prima l'id, poi la schermata: e' la coppia canonica, e il ramo che
          disegna il dettaglio e' guardato da `selectedTournamentId`. */}
      <BandaGaraInCorso
        onApriTorneo={(id) => { setSelectedTournamentId(id); setScreen('tournament-detail'); }}
        onApriArena={(id) => { setSelectedArenaId(id); setScreen('arena-detail'); }}
        /* E chi e' GIA' sulla pagina della sua gara non ha bisogno che gliela si
           annunci: li' il banner taceva invece di coprire la banda dei bottoni.
           Si passa la posizione gia' memoizzata — una sola verita' su «dove
           sono», la stessa che governa il gesto indietro. */
        pagina={location}
      />
      {/* E quando l'attesa finisce da sola — tavolo scaduto, o partita annullata
          perché l'avversario è uscito prima della prima mossa — qualcuno deve
          dirlo. Stesso angolo del banner qui sopra, che in quel momento è già
          sparito: i due non compaiono mai insieme. */}
      <AvvisoLobby />
      {/* Lo stesso account si e' collegato da un altro dispositivo e il server ha
          chiuso la connessione qui: senza questo la schermata restava muta,
          qualunque fosse. «Usa qui» la riprende. */}
      <AvvisoAltroDispositivo />
      <PlayOverlay
        isOpen={showLobbyOverlay}
        onClose={() => setShowLobbyOverlay(false)}
        onPlayInPerson={handlePlayInPerson}
        onPlayAI={(variant, difficulty, options) => handlePlayAI(variant, difficulty, 'none', options)}
        onSendChallenge={handleChallengePlayer}
        onCreateChallengeLink={(settings) => { void handleCreateChallengeLink(settings); }}
        onOpenOpeningTables={openOpeningTables}
        onSignIn={apriAccesso}
        /* La stessa coppia della home, perche' e' la stessa porta: «Gioca» della
           barra e «Gioca subito» della card dei punteggi aprono questo foglio, e
           sul desktop lo aprono con la lista di chi c'e' accanto. */
        pannelloAccanto={coppiaAffiancata ? ((variante) => (
          <ConnectedUsersModal
            isOpen
            affiancato
            onClose={() => setShowLobbyOverlay(false)}
            onSignIn={apriAccesso}
            /* Di quale punteggio parla la lista: la variante e' quella del menu
               accanto — la card dei punteggi qui non c'e', ma il menu si', ed e'
               la scelta che l'utente ha appena fatto e vede. La cadenza resta il
               ripiego sulle preferenze: in quel menu la cadenza non e' una scelta
               che resta a schermo, premerla pubblica un tavolo. */
            ratingBucket={ratingBucketFrom(variante, null, { variant: preferredVariant, cadence: preferredCadence })}
            onChallenge={(user) => {
              // Si chiude la coppia e si apre la sfoglia gia' puntata su di lui:
              // la stessa sequenza che la home fa da «collegati» a «sfida».
              setShowLobbyOverlay(false);
              setCollegatoSfidato(user);
              setAvversarioSfoglia('challenge');
            }}
            /* Chi ha un tavolo aperto si affronta subito, al suo tavolo: la
               coppia non si chiude qui, ci pensa l'effetto sul cambio
               schermata quando la partita parte. */
            onSitAtTable={sitAtLobbyTable}
            // Il lobby globale lo chiude gia' l'effetto sul cambio schermata.
            onSpectate={(user) => handleSpectatePlayer(user.userId)}
          />
        )) : undefined}
      />
      {/* Il menu della voce «Gioca» della barra, e a ogni larghezza: e' l'indice
          unico delle porte del gioco, da quando le due hero — dashboard e
          landing — hanno un invito solo. Montava solo a ≤768px finche' sul
          desktop quelle porte erano tutte visibili nella hero. */}
      <FoglioGioca
        isOpen={foglioGiocaAperto}
        onClose={() => setFoglioGiocaAperto(false)}
        /* Senza `dalFoglioGioca` nel valore, e NON e' una dimenticanza: questa
           riga puo' dover chiedere una sessione ospite prima di avere qualcosa
           da aprire, e chiudere il foglio prima di sapere se ci riesce vorrebbe
           dire aspettare al buio e non avere dove dire di no. La chiusura e' una
           DECISIONE della porta, presa quando c'e' dove andare: l'invariante
           regge per indirezione, attraverso `vaiAlLobbyDalFoglio`. */
        onPlayOnline={apriGiocaOnline}
        /* Lo stato della prima riga. Senza queste due, l'attesa e il motivo del
           rifiuto non arrivano a schermo: una prop che nessuno passa non rompe
           niente, non accende nessun rosso, e si scopre a mano settimane dopo
           (la lezione delle bandierine di `addPlayer`). */
        playPending={faseOnline !== 'ferma'}
        guestErrorKey={erroreOspiteDalFoglio}
        onDirectChallenge={() => dalFoglioGioca(
          // L'ospite entra: la sfoglia ha gia' il suo pannello, che spiega
          // perche' non puo' sfidare e offre l'accesso. Il visitatore no —
          // senza sessione non c'e' nessuna lista di giocatori da mostrargli.
          isAuthenticated ? () => setAvversarioSfoglia('challenge') : apriAccesso,
        )}
        onPlayAI={() => dalFoglioGioca(() => setAvversarioSfoglia('computer'))}
        onPlayInPerson={() => dalFoglioGioca(() => setAvversarioSfoglia('inperson'))}
        /*
         * Il problema del giorno vuole un account vero, e lo DICE a schermo.
         *
         * Entrambi i rami passano da `dalFoglioGioca`, come la riga accanto:
         * quello che va porta alla lista, quello che rifiuta alza
         * `OverlaySoloIscritti`. E' la forma della sfida diretta, che per lo
         * stesso rifiuto apre una scheda da sempre.
         *
         * C'e' stato un passaggio intermedio in cui il rifiuto restava DENTRO il
         * foglio, in un riquadro sotto la riga toccata: nasceva per non chiudere
         * la porta in faccia senza una parola, ma quella riga e' la quinta di
         * sei e a 360x640 la risposta doveva portarsi in vista da se'. Un ramo
         * solo per l'ospite e per il visitatore: dietro questa porta non c'e'
         * niente da mostrare ne' all'uno ne' all'altro.
         */
        onDailyPuzzle={() => dalFoglioGioca(
          isAuthenticated && !isGuest ? apriProblemiDelGiorno : () => setPuzzleSoloIscritti(true),
        )}
        onTournaments={() => dalFoglioGioca(() => setScreen('tournaments'))}
      />
      {/* Il menu di «Strumenti», fratello del precedente e montato qui per la
          stessa ragione: la quarta voce della barra si vede da quasi ovunque.
          Il gate dell'editor sta QUI e non nella riga, perche' questo e'
          l'unico posto che sa chi siamo — ed e' lo stesso gate che in Sidebar
          decide se la voce di barra esiste. Chi non ha un conto vero non vede
          nemmeno il menu, quindi la guardia e' una rete, non un bivio. */}
      <FoglioStrumenti
        isOpen={strumentiAperto}
        onClose={() => setStrumentiAperto(false)}
        onEditor={() => dalFoglioStrumenti(() => {
          if (isAuthenticated && !isGuest) setScreen('editor');
        })}
        onOrologio={() => dalFoglioStrumenti(() => setScreen('orologio'))}
        onEstraiApertura={() => dalFoglioStrumenti(() => setScreen('estrai-apertura'))}
      />
      {/* La sfoglia della partita personalizzata aperta dal foglio, gia' puntata
          sulla riga toccata. Fratello del foglio e non figlio, come la coppia
          PlayOverlay/CustomTableHost: annidata, ogni click al suo interno
          risalirebbe fino alla chiusura del foglio. */}
      <CustomTableHost
        isOpen={avversarioSfoglia !== null}
        onClose={() => { setAvversarioSfoglia(null); setCollegatoSfidato(null); }}
        defaultVariant={getLastVariant() ?? preferredVariant ?? 'international'}
        defaultOpponent={avversarioSfoglia ?? undefined}
        // Chi e' stato scelto nella lista accanto al lobby; dal foglio delle porte
        // non c'e' nessuno di scelto e la riga «Avversario» parte vuota.
        preselectedUser={collegatoSfidato ?? undefined}
        // Come la Sfida diretta della dashboard e della landing: la sfoglia
        // riparte da dove l'utente l'aveva lasciata (utils/last-used-play).
        rememberSettings
        onPlayInPerson={handlePlayInPerson}
        onPlayAI={(variant, difficulty, options) => handlePlayAI(variant, difficulty, 'none', options)}
        onSendChallenge={handleChallengePlayer}
        onCreateChallengeLink={(settings) => { void handleCreateChallengeLink(settings); }}
        onOpenOpeningTables={openOpeningTables}
        onSignIn={apriAccesso}
      />
      {/* Il rifiuto della porta dei problemi del giorno, nella forma che ha la
          sfida diretta. Fratello del foglio come la sfoglia qui sopra, e per la
          stessa ragione: annidato, ogni click al suo interno risalirebbe fino
          alla chiusura del foglio.
          Nessuna delle due maniglie chiude l'overlay — il modale che aprono sale
          al piano sopra (`--z-accesso`) ed e' lui che il gesto «indietro» chiude
          per primo. `apriRegistrazione` e non la registrazione nuda: per un
          ospite dirotta sulla conversione, che tiene la sua riga `users` e con
          essa le partite che ha giocato. */}
      <OverlaySoloIscritti
        isOpen={puzzleSoloIscritti}
        onClose={() => setPuzzleSoloIscritti(false)}
        titolo={t('dashboard.hero.ctaDailyPuzzle')}
        messaggioKey="puzzle.guest.locked"
        onSignIn={apriAccesso}
        onRegister={apriRegistrazione}
      />
    </>
  );

  // Daily-puzzle full-screen sheet (mobile only — desktop uses the 'puzzle'
  // screen). Self-gates on isOpen, so it's inert until a puzzle is opened.
  const puzzleOverlay = (
    <DashboardPuzzleModal
      isOpen={puzzleOverlayId != null || puzzleOverlayDemo}
      puzzleId={puzzleOverlayId ?? 0}
      practice={puzzleOverlayPractice}
      localPuzzle={puzzleOverlayDemo ? DEMO_INT_PUZZLE : undefined}
      onRegister={registerFromPuzzle}
      canShare={isAuthenticated && !isGuest}
      onContinueVsAI={handleContinuePuzzleVsAI}
      onClose={() => { setPuzzleOverlayId(null); setPuzzleOverlayDemo(false); }}
    />
  );

  // Shared leave-game confirmation modal. Rendered on every game screen so the
  // back-gesture guard (useBackNavigation -> onGuardedBack) can reuse it.
  const leaveConfirmModal = leaveConfirmScreen && (
    <div
      className="auth-modal-overlay auth-modal-overlay--conferma"
      onClick={cancelLeave}
    >
      <div
        className="auth-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 360, textAlign: 'center' }}
      >
        <h2 style={{ marginBottom: 'var(--spacing-md)', fontSize: '1.3rem' }}>
          {t('game.confirmLeaveTitle')}
        </h2>
        <p style={{
          color: 'var(--color-text-secondary)',
          fontSize: '0.95rem',
          lineHeight: 1.5,
          marginBottom: 'var(--spacing-lg)',
        }}>
          {t('game.confirmLeave')}
        </p>
        <div style={{ display: 'flex', gap: 'var(--spacing-md)' }}>
          <button
            type="button"
            onClick={confirmLeave}
            style={{
              flex: 1,
              padding: 'var(--spacing-md)',
              borderRadius: 8,
              border: 'none',
              background: 'var(--color-error, #e74c3c)',
              color: 'white',
              fontSize: '1rem',
              fontWeight: 600,
              cursor: 'pointer',
              minHeight: 44,
            }}
          >
            {t('game.confirmLeaveButton')}
          </button>
          <button
            type="button"
            onClick={cancelLeave}
            style={{
              flex: 1,
              padding: 'var(--spacing-md)',
              borderRadius: 8,
              border: '1px solid rgba(255,255,255,0.2)',
              background: 'transparent',
              color: 'var(--color-text)',
              fontSize: '1rem',
              fontWeight: 600,
              cursor: 'pointer',
              minHeight: 44,
            }}
          >
            {t('game.confirmLeaveCancel')}
          </button>
        </div>
      </div>
    </div>
  );

  // Redirect legacy 'landing' to 'home'
  if (screen === 'landing') {
    setScreen('home');
    return null;
  }

  // Home Page (with sidebar)
  if (screen === 'home') {
    return (
      <Layout
        activeItem={getActiveNavItem()}
        onNavigate={handleNavigate}
        isAuthenticated={isAuthenticated}
        isGuest={isGuest}
        onLogout={handleLogout}
        onLoginClick={apriAccesso}
        showSidebar={shouldShowSidebar}
      >
        <AvvisoSenzaRete contesto="home" />
        <AvvisoDiServizio />
        {isGuest && <GuestBanner onUpgrade={() => {  setShowUpgradeModal(true); }} />}
        {isAuthenticated && !isGuest && user ? (
          <Suspense fallback={null}>
            <DashboardPage
              userId={user.id}
              username={user.username}
              avatar={user.avatar}
              onPlayAI={(variant, difficulty, timeControl, options) => handlePlayAI(variant as import('@draughts/shared').GameVariant, difficulty as 'very-easy' | 'easy' | 'medium' | 'hard' | 'expert' | 'master', timeControl as import('@draughts/shared').TimeControlPreset, options)}
              onPlayInPerson={handlePlayInPerson}
              onChallenge={handleChallengePlayer}
              onSitAtTable={sitAtLobbyTable}
              onCreateChallengeLink={handleCreateChallengeLink}
              onOpenOpeningTables={openOpeningTables}
              onSpectatePlayer={handleSpectatePlayer}
              onNavigateToProfile={() => { openProfile(user.username); }}
              onOpenOwnProfile={openProfile}
              onOpenLeaderboard={(v, d) => { openLeaderboard('mostGames', v, d); }}
              onNavigateToSettings={() => { openProfile(user.username, 'settings'); }}
              onLogout={handleLogout}
              onNavigateToArena={(id) => { setSelectedArenaId(id); setScreen('arena-detail'); }}
              onNavigateToTournament={(id) => { setSelectedTournamentId(id); setScreen('tournament-detail'); }}
              onNavigateToTournaments={() => { setScreen('tournaments'); }}
              onSolvePuzzle={openPuzzle}
              dailyPuzzlesOpen={showDailyPuzzles}
              onOpenDailyPuzzles={() => setShowDailyPuzzles(true)}
              onCloseDailyPuzzles={() => setShowDailyPuzzles(false)}
              onSolvePuzzleFromDailyList={openPuzzleFromDailyList}
              puzzleSheetOpen={puzzleOverlayId != null || puzzleOverlayDemo}
              onReplayGame={(id) => { setReplayGameId(id); setScreen('games-history'); }}
              onNavigateToFaq={openFaq}
              initialPlay={pendingPlay}
              onInitialPlayConsumed={() => setPendingPlay(null)}
            />
          </Suspense>
        ) : (
          <HomePage
            isAuthenticated={isAuthenticated}
            isGuest={isGuest}
            user={user}
            onPlayAI={handlePlayAI}
            onPlayInPerson={handlePlayInPerson}
            onChallengePlayer={handleChallengePlayer}
            onCreateChallengeLink={handleCreateChallengeLink}
            onOpenOpeningTables={openOpeningTables}
            onSpectatePlayer={handleSpectatePlayer}
            onLoginRequired={apriAccesso}
            onCreateAccount={apriRegistrazione}
            onPlayAsGuest={handlePlayAsGuest}
            onNavigateToProfile={() => { if (user) { openProfile(user.username); } }}
            onNavigateToTournament={(id) => { setSelectedTournamentId(id); setScreen('tournament-detail'); }}
            onSolvePuzzle={openPuzzle}
            onOpenDemoPuzzle={openDemoPuzzle}
            onOpenVariantRules={(v) => { setRulesVariant(v); setScreen('rules-variant'); }}
            onOpenGameRules={() => setScreen('rules-game')}
            initialPlay={pendingPlay}
            onInitialPlayConsumed={() => setPendingPlay(null)}
          />
        )}
        <LegalFooter
          onNavigateToTerms={() => setScreen('terms')}
          onNavigateToPrivacy={() => setScreen('privacy')}
          onNavigateToFaq={() => { openFaq(); }}
          onNavigateToRules={() => setScreen('rules-index')}
        />
        {cookieBanner}
        {challengeOverlays}
        {puzzleOverlay}
        {avvisoRientro}
      </Layout>
    );
  }

  // Local Game (no sidebar, full screen - uses GameScreen via LocalGame)
  if (screen === 'local-game') {
    return (
      <div className="game-fullscreen">
        <LocalGame onLeave={handleLeaveLocalGame} />
        {leaveConfirmModal}
        {cookieBanner}
        {challengeOverlays}
        {puzzleOverlay}
      </div>
    );
  }

  // Online Game (no sidebar, full screen)
  if (screen === 'online-game') {
    return (
      <div className="game-fullscreen">
        <OnlineGame onLeave={() => {  leaveGameTo('home'); }} />
        {leaveConfirmModal}
        {cookieBanner}
        {challengeOverlays}
        {puzzleOverlay}
      </div>
    );
  }

  // Spectating someone else's live game (no sidebar, full screen).
  // `challengeOverlays` matters more here than anywhere: watching is exactly
  // when a user is idle and challengeable, so an incoming challenge must be
  // visible. Accepting it flips the multiplayer status to 'playing', and the
  // auto-nav effect above pulls them out of here into their own game —
  // 'spectate' is deliberately absent from `activeGameScreens` for that reason.
  if (screen === 'spectate' && spectateTargetUserId) {
    return (
      <div className="game-fullscreen">
        <Suspense fallback={null}>
          <SpectateGame targetUserId={spectateTargetUserId} onLeave={handleLeaveSpectate} />
        </Suspense>
        {cookieBanner}
        {challengeOverlays}
        {puzzleOverlay}
      </div>
    );
  }

  // Profile screen
  if (screen === 'profile' && profileUsername) {
    return (
      <Layout
        activeItem={getActiveNavItem()}
        onNavigate={handleNavigate}
        isAuthenticated={isAuthenticated}
        isGuest={isGuest}
        onLogout={handleLogout}
        onLoginClick={apriAccesso}
        showSidebar={shouldShowSidebar}
      >
        <AvvisoSenzaRete />
        <Suspense fallback={null}>
          <ProfilePage
            // Remount on a different user: without this the games filters
            // survive the switch, and a folder filter (which queries YOUR
            // library) would list your saved games under somebody else's
            // profile — with no way to clear it, since the folder filter isn't
            // rendered when the profile isn't yours.
            key={profileUsername}
            username={profileUsername}
            initialTab={profileTab ?? undefined}
            // Da dove si era venuti (il torneo, la classifica…), come la freccia
            // del browser. La home solo se prima non c'e' niente.
            onBack={() => { tornaIndietro(() => setScreen('home')); }}
            onLogout={handleLogout}
          />
        </Suspense>
        {cookieBanner}
        {challengeOverlays}
        {puzzleOverlay}
      </Layout>
    );
  }

  // Games History screen
  if (screen === 'games-history') {
    return (
      <Layout
        activeItem={getActiveNavItem()}
        onNavigate={handleNavigate}
        isAuthenticated={isAuthenticated}
        isGuest={isGuest}
        onLogout={handleLogout}
        onLoginClick={apriAccesso}
        showSidebar={shouldShowSidebar}
      >
        <Suspense fallback={null}>
          <GamesHistoryPage initialGameId={replayGameId} onBack={() => { setReplayGameId(null); setScreen('home'); }} />
        </Suspense>
        {cookieBanner}
        {challengeOverlays}
        {puzzleOverlay}
      </Layout>
    );
  }

  // Leaderboard screen
  if (screen === 'leaderboard') {
    return (
      <Layout
        activeItem={getActiveNavItem()}
        onNavigate={handleNavigate}
        isAuthenticated={isAuthenticated}
        isGuest={isGuest}
        onLogout={handleLogout}
        onLoginClick={apriAccesso}
        showSidebar={shouldShowSidebar}
      >
        <Suspense fallback={null}>
          <LeaderboardPage
            // Remount when arriving with a requested tab/variant: those are
            // seeds for useState, so a live instance would ignore them.
            key={`${leaderboardTab ?? ''}:${leaderboardVariant ?? ''}:${leaderboardPeriod === undefined ? '' : leaderboardPeriod ?? 'all'}`}
            initialTab={(leaderboardTab as 'allTime' | 'mostGames' | 'streak' | null) ?? undefined}
            initialVariant={leaderboardVariant ?? undefined}
            initialPeriodDays={leaderboardPeriod}
            onOpenOwnProfile={(username) => {
              openProfile(username);
            }}
          />
        </Suspense>
        {cookieBanner}
        {challengeOverlays}
        {puzzleOverlay}
      </Layout>
    );
  }


  // Arena Detail screen
  if (screen === 'arena-detail' && selectedArenaId) {
    return (
      <Layout
        activeItem={getActiveNavItem()}
        onNavigate={handleNavigate}
        isAuthenticated={isAuthenticated}
        isGuest={isGuest}
        onLogout={handleLogout}
        onLoginClick={apriAccesso}
        showSidebar={shouldShowSidebar}
      >
        <Suspense fallback={null}>
          <ArenaDetailPage
            arenaId={selectedArenaId}
            onBack={() => {
              setSelectedArenaId(null);
              /* L'elenco delle arene non esiste piu' come schermata a se': le
                 arene sono righe dell'elenco dei tornei, sotto il filtro
                 «Formato». Tornare indietro torna LA'. */
              setScreen('tournaments');
            }}
            onResumeGame={() => { setScreen('arena-game'); }}
            onSpectatePlayer={handleSpectateFromArena}
            inviteToken={inviteArenaToken}
            /*
             * Una porta sola, ospite compreso. Prima l'ospite finiva
             * sull'upgrade — che gli conserva partite e conto — ma il bottone
             * gli diceva «Crea Account» mentre tutto il resto dell'app gli dice
             * «Accedi»: due nomi per la stessa cosa in due schermate diverse.
             * L'upgrade resta dov'e' sempre stato, nel banner dell'ospite.
             */
            onSignIn={apriAccesso}
          />
        </Suspense>
        {cookieBanner}
        {challengeOverlays}
        {puzzleOverlay}
      </Layout>
    );
  }

  // Arena Results screen — il podio, per chi ci ha giocato
  if (screen === 'arena-results') {
    return (
      <Layout
        activeItem={getActiveNavItem()}
        onNavigate={handleNavigate}
        isAuthenticated={isAuthenticated}
        isGuest={isGuest}
        onLogout={handleLogout}
        onLoginClick={apriAccesso}
        showSidebar={shouldShowSidebar}
      >
        <Suspense fallback={null}>
          <ArenaFinalResultsPage
            onBack={() => {
              setSelectedArenaId(null);
              setScreen('tournaments');
            }}
            onNextArena={() => {
              setSelectedArenaId(null);
              setScreen('tournaments');
            }}
          />
        </Suspense>
        {cookieBanner}
        {challengeOverlays}
        {puzzleOverlay}
      </Layout>
    );
  }

  // Arena Game screen (no sidebar, fullscreen like tournament game)
  if (screen === 'arena-game') {
    return (
      <div className="game-fullscreen">
        <ArenaGame
          onBackToLobby={() => {  leaveGameTo('arena-detail'); }}
        />
        {leaveConfirmModal}
        {cookieBanner}
        {challengeOverlays}
        {puzzleOverlay}
      </div>
    );
  }

  // Email Verification screen
  if (screen === 'verify-email' && verifyToken) {
    return (
      <Layout
        activeItem={getActiveNavItem()}
        onNavigate={handleNavigate}
        isAuthenticated={isAuthenticated}
        isGuest={isGuest}
        onLogout={handleLogout}
        onLoginClick={apriAccesso}
        showSidebar={true}
      >
        <VerifyEmailPage
          token={verifyToken}
          onComplete={() => {
            setVerifyToken(null);
            setScreen('home');
            /*
             * La schermata d'accesso si apre solo se la verifica NON e'
             * andata a buon fine: dopo una verifica riuscita la sessione e'
             * gia' aperta, e chiedere di accedere manderebbe a rifare una
             * cosa appena fatta.
             */
            if (!useAuthStore.getState().isAuthenticated) apriAccesso();
          }}
          onTornaAlTorneo={(id, gettone) => {
            setVerifyToken(null);
            setSelectedTournamentId(id);
            setInviteTournamentToken(gettone);
            setInviteTournamentId(id);
            setScreen('tournament-detail');
          }}
        />
        {cookieBanner}
        {challengeOverlays}
        {puzzleOverlay}
      </Layout>
    );
  }

  // Daily-puzzle invite screen (arrived on /?puzzle=daily&variant=…, typically
  // from WhatsApp). The daily puzzle is registered-only, so a guest or logged-out
  // recipient is asked to sign in here instead of being handed the board; the
  // resolver above opens the puzzle by itself the moment they authenticate.
  if (screen === 'puzzle-invite' && dailyDeepLinkVariant) {
    return (
      <Layout
        activeItem={getActiveNavItem()}
        onNavigate={handleNavigate}
        isAuthenticated={isAuthenticated}
        isGuest={isGuest}
        onLogout={handleLogout}
        onLoginClick={apriAccesso}
        showSidebar={true}
      >
        {/* Anche qui, e non solo su `screen === 'puzzle'`: e' DOVE ATTERRA un
            link condiviso ai problemi, e senza rete e' la schermata che si vede
            davvero. Il messaggio di sotto dice «forse per questa variante non ce
            n'e' uno, oppure e' caduta la connessione» — due cause in una frase,
            e l'avviso scioglie l'ambiguita' invece di lasciarla all'utente. */}
        <AvvisoSenzaRete contesto="dati" />
        {dailyDeepLinkState === 'error-link' ? (
          <div className="pz-page">
            <div className="pz-inner">
              <PuzzleUnavailable
                reason="link"
                onRetry={() => {
                  dailyDeepLinkDone.current = false;
                  setDailyDeepLinkState('resolving');
                  setDeepLinkAttempt((n) => n + 1);
                }}
                onRegister={registerFromPuzzle}
                onHome={leaveDailyPuzzleGate}
              />
            </div>
          </div>
        ) : (
          <DailyPuzzleInvitePage
            variant={dailyDeepLinkVariant}
            // Only the explicit 'gate' state shows the sign-up pitch. Anything
            // else here means we are still deciding — and a registered
            // recipient crosses this screen on the way to the board.
            checking={dailyDeepLinkState !== 'gate'}
            // Guests go to the UPGRADE modal, not plain register: upgrading keeps
            // the same user row, so nothing they have already earned is lost.
            onCreateAccount={registerFromPuzzle}
            onLogin={apriAccesso}
            onTryDemo={openDemoPuzzle}
            onLeave={leaveDailyPuzzleGate}
          />
        )}
        {cookieBanner}
        {challengeOverlays}
        {puzzleOverlay}
      </Layout>
    );
  }

  // Challenge invite screen (arrived on /?challenge=<id>, typically from WhatsApp)
  if (screen === 'challenge-invite' && inviteLinkId) {
    return (
      <Layout
        activeItem={getActiveNavItem()}
        onNavigate={handleNavigate}
        isAuthenticated={isAuthenticated}
        isGuest={isGuest}
        onLogout={handleLogout}
        onLoginClick={apriAccesso}
        showSidebar={true}
      >
        <ChallengeInvitePage
          linkId={inviteLinkId}
          onPlayAsGuest={handlePlayAsGuest}
          onLoginRequired={apriAccesso}
          onLeave={() => {
            setInviteLinkId(null);
            homeScrollRef.current = 0;
            setScreen('home');
          }}
        />
        {cookieBanner}
        {challengeOverlays}
        {puzzleOverlay}
      </Layout>
    );
  }

  // Password Reset screen
  if (screen === 'reset-password' && resetToken) {
    return (
      <Layout
        activeItem={getActiveNavItem()}
        onNavigate={handleNavigate}
        isAuthenticated={isAuthenticated}
        isGuest={isGuest}
        onLogout={handleLogout}
        onLoginClick={apriAccesso}
        showSidebar={true}
      >
        <ResetPasswordPage
          token={resetToken}
          onComplete={() => {
            setResetToken(null);
            setScreen('home');
            apriAccesso();
          }}
        />
        {cookieBanner}
        {challengeOverlays}
        {puzzleOverlay}
      </Layout>
    );
  }

  // Puzzle screen
  if (screen === 'puzzle') {
    return (
      <Layout
        activeItem={getActiveNavItem()}
        onNavigate={handleNavigate}
        isAuthenticated={isAuthenticated}
        isGuest={isGuest}
        onLogout={handleLogout}
        onLoginClick={apriAccesso}
        showSidebar={shouldShowSidebar}
      >
        <AvvisoSenzaRete contesto="dati" />
        {isGuest && <GuestBanner onUpgrade={() => { setShowUpgradeModal(true); }} />}
        <PuzzlePage onBack={() => { setPuzzleDemo(false); setScreen('home'); }} initialPuzzleId={selectedPuzzleId ?? undefined} isDaily={!puzzleDemo && selectedPuzzleId != null} practice={puzzlePractice} localPuzzle={puzzleDemo ? DEMO_INT_PUZZLE : undefined} onRegister={registerFromPuzzle} canShare={isAuthenticated && !isGuest} onContinueVsAI={handleContinuePuzzleVsAI} />
        {/* A guest can land straight here from a shared puzzle link, so this
            screen needs the same register/upgrade modals the home screen has. */}
        {cookieBanner}
        {challengeOverlays}
        {puzzleOverlay}
      </Layout>
    );
  }

  // DEV-ONLY daily-puzzle preview screen. Guarded by import.meta.env.DEV so both
  // the branch and the lazy PuzzlePreviewPage chunk are absent from prod builds.
  if (import.meta.env.DEV && screen === 'puzzle-preview' && PuzzlePreviewPage) {
    const PreviewScreen = PuzzlePreviewPage;
    return (
      <Layout
        activeItem={getActiveNavItem()}
        onNavigate={handleNavigate}
        isAuthenticated={isAuthenticated}
        isGuest={isGuest}
        onLogout={handleLogout}
        onLoginClick={apriAccesso}
        showSidebar={shouldShowSidebar}
      >
        <Suspense fallback={null}>
          <PreviewScreen initialVariant={previewVariant} initialDate={previewDate} onBack={() => setScreen('home')} />
        </Suspense>
        {cookieBanner}
        {challengeOverlays}
        {puzzleOverlay}
      </Layout>
    );
  }

  // Terms of Service screen
  if (screen === 'terms') {
    return (
      <Layout
        activeItem={getActiveNavItem()}
        onNavigate={handleNavigate}
        isAuthenticated={isAuthenticated}
        isGuest={isGuest}
        onLogout={handleLogout}
        onLoginClick={apriAccesso}
        showSidebar={shouldShowSidebar}
      >
        <TermsPage />
        {cookieBanner}
        {challengeOverlays}
        {puzzleOverlay}
      </Layout>
    );
  }

  // Privacy Policy screen
  if (screen === 'privacy') {
    return (
      <Layout
        activeItem={getActiveNavItem()}
        onNavigate={handleNavigate}
        isAuthenticated={isAuthenticated}
        isGuest={isGuest}
        onLogout={handleLogout}
        onLoginClick={apriAccesso}
        showSidebar={shouldShowSidebar}
      >
        <PrivacyPage />
        {cookieBanner}
        {challengeOverlays}
        {puzzleOverlay}
      </Layout>
    );
  }

  // FAQ & Contact screen
  if (screen === 'faq') {
    return (
      <Layout
        activeItem={getActiveNavItem()}
        onNavigate={handleNavigate}
        isAuthenticated={isAuthenticated}
        isGuest={isGuest}
        onLogout={handleLogout}
        onLoginClick={apriAccesso}
        showSidebar={shouldShowSidebar}
      >
        <Suspense fallback={null}>
          <FaqPage
            onBack={() => {  setScreen('home'); }}
            initialEntryId={faqEntry ?? undefined}
            onOpenTournamentRules={openTournamentRules}
          />
        </Suspense>
        {cookieBanner}
        {challengeOverlays}
        {puzzleOverlay}
      </Layout>
    );
  }

  // Rules index screen: il bivio fra regole di gioco e regole dei tornei
  if (screen === 'rules-index') {
    return (
      <Layout
        activeItem={getActiveNavItem()}
        onNavigate={handleNavigate}
        isAuthenticated={isAuthenticated}
        isGuest={isGuest}
        onLogout={handleLogout}
        onLoginClick={apriAccesso}
        showSidebar={shouldShowSidebar}
      >
        <RulesIndexPage
          onOpenGameRules={() => {  setScreen('rules-game'); }}
          onOpenTournamentRules={() => { openTournamentRules(); }}
        />
        {cookieBanner}
        {challengeOverlays}
        {puzzleOverlay}
      </Layout>
    );
  }

  // Game rules screen: la griglia delle ventiquattro varianti, che fino a ieri
  // stava sull'indice. Sotto-pagina dell'indice, non di una variante.
  if (screen === 'rules-game') {
    return (
      <Layout
        activeItem={getActiveNavItem()}
        onNavigate={handleNavigate}
        isAuthenticated={isAuthenticated}
        isGuest={isGuest}
        onLogout={handleLogout}
        onLoginClick={apriAccesso}
        showSidebar={shouldShowSidebar}
      >
        <RulesGamePage
          onBack={() => {  setScreen('rules-index'); }}
          onSelectVariant={(v) => {  setRulesVariant(v); setScreen('rules-variant'); }}
          preferred={preferredVariant}
        />
        {cookieBanner}
        {challengeOverlays}
        {puzzleOverlay}
      </Layout>
    );
  }

  // Tournament rules screen (a sub-page of the rules index, not of a variant)
  if (screen === 'rules-tournaments') {
    return (
      <Layout
        activeItem={getActiveNavItem()}
        onNavigate={handleNavigate}
        isAuthenticated={isAuthenticated}
        isGuest={isGuest}
        onLogout={handleLogout}
        onLoginClick={apriAccesso}
        showSidebar={shouldShowSidebar}
      >
        <RulesTournamentsPage
          onBack={() => {  setScreen('rules-index'); }}
          initialAnchor={tournamentAnchor ?? undefined}
        />
        {cookieBanner}
        {challengeOverlays}
        {puzzleOverlay}
      </Layout>
    );
  }

  // Per-variant rules screen
  if (screen === 'rules-variant' && rulesVariant) {
    return (
      <Layout
        activeItem={getActiveNavItem()}
        onNavigate={handleNavigate}
        isAuthenticated={isAuthenticated}
        isGuest={isGuest}
        onLogout={handleLogout}
        onLoginClick={apriAccesso}
        showSidebar={shouldShowSidebar}
      >
        <RulesPage
          variant={rulesVariant}
          onBack={() => {  setScreen('rules-game'); }}
          onSelectVariant={(v) => {  setRulesVariant(v); setScreen('rules-variant'); }}
          onOpenOpenings={() => {  setScreen('rules-openings'); }}
          onOpenCaptureRules={() => {  setScreen('rules-capture'); }}
          onPlay={handlePlayFromRules}
          preferred={preferredVariant}
        />
        {cookieBanner}
        {challengeOverlays}
        {puzzleOverlay}
      </Layout>
    );
  }

  // Opening tables of a variant with an official F.I.D. ballot (italian / english).
  if (screen === 'rules-openings' && rulesVariant) {
    return (
      <Layout
        activeItem={getActiveNavItem()}
        onNavigate={handleNavigate}
        isAuthenticated={isAuthenticated}
        isGuest={isGuest}
        onLogout={handleLogout}
        onLoginClick={apriAccesso}
        showSidebar={shouldShowSidebar}
      >
        <RulesOpeningsPage
          variant={rulesVariant}
          onBack={() => {  setScreen('rules-variant'); }}
        />
        {cookieBanner}
        {challengeOverlays}
        {puzzleOverlay}
      </Layout>
    );
  }

  // Detailed capture rules of a variant whose capture precedence is a rule set
  // in its own right (italian, triestine) or the free-choice counter-example
  // (english).
  if (screen === 'rules-capture' && rulesVariant) {
    return (
      <Layout
        activeItem={getActiveNavItem()}
        onNavigate={handleNavigate}
        isAuthenticated={isAuthenticated}
        isGuest={isGuest}
        onLogout={handleLogout}
        onLoginClick={apriAccesso}
        showSidebar={shouldShowSidebar}
      >
        <RulesCapturePage
          variant={rulesVariant}
          onBack={() => {  setScreen('rules-variant'); }}
        />
        {cookieBanner}
        {challengeOverlays}
        {puzzleOverlay}
      </Layout>
    );
  }

  // Board editor screen (compose a position → analyze / play vs AI / challenge a friend).
  if (screen === 'editor') {
    return (
      <Layout
        activeItem={getActiveNavItem()}
        onNavigate={handleNavigate}
        isAuthenticated={isAuthenticated}
        isGuest={isGuest}
        onLogout={handleLogout}
        onLoginClick={apriAccesso}
        showSidebar={shouldShowSidebar}
      >
        <Suspense fallback={null}>
          <EditorScreen
            onPlayVsAI={handleContinuePuzzleVsAI}
            onChallengePlayer={handleChallengePlayer}
            onCreateChallengeLink={handleCreateChallengeLink}
            isAuthenticated={isAuthenticated}
            isGuest={isGuest}
            onRequireLogin={apriAccesso}
          />
        </Suspense>
        {cookieBanner}
        {challengeOverlays}
        {puzzleOverlay}
      </Layout>
    );
  }

  /*
   * I due attrezzi del menu «Strumenti», gemelli dell'editor qui sopra: gli
   * overlay globali restano, e non hanno un URL proprio — `contentUrlFor` li
   * lascia sulla landing. Il gesto indietro funziona comunque, perche'
   * l'osservatore della cronologia confronta la POSIZIONE e non l'indirizzo
   * (`sameLocation`, in useBackNavigation): tre schermate sullo stesso URL sono
   * tre voci distinte.
   *
   * L'OROLOGIO E' L'ECCEZIONE SULLA BARRA. Negli altri due attrezzi la barra
   * resta, perche' e' la loro uscita; qui, sul telefono e MENTRE CORRE, si
   * smonta: e' un orologio appoggiato fra due giocatori, e quei 64px sono
   * altezza di quadrante. Torna in pausa, a tempo scaduto e davanti alle
   * impostazioni (che mettono in pausa), cioe' in tutte le fasi in cui c'e'
   * qualcosa da fare che non sia guardare le cifre. L'uscita mentre corre e'
   * «Pausa», che sta sempre a schermo fra i due quadranti, piu' il gesto
   * indietro.
   *
   * Si SMONTA e non si nasconde: `body:not(:has(.sidebar))` e' il modo in cui
   * questo progetto chiede «c'e' una barra?» (BandaGaraInCorso.css), e una barra
   * `display: none` resterebbe nel DOM a mentirgli. Smontandola cade da se'
   * anche il `padding-bottom: var(--altezza-barra)` di `.layout.with-sidebar`,
   * che lascerebbe una striscia morta in fondo.
   *
   * ⚠️ `<main>` e la sua posizione non cambiano — `Layout` rende due posizioni
   * fisse, non una lista con chiavi — quindi `OrologioScreen` NON si rimonta e
   * le cifre continuano a correre. Se si rimontasse, la macchina tornerebbe a
   * 'pronto', `onCorsa(false)` farebbe ricomparire la barra, e si avrebbe un
   * batter d'ali fra le due condizioni.
   */
  if (screen === 'orologio') {
    return (
      <Layout
        activeItem={getActiveNavItem()}
        onNavigate={handleNavigate}
        isAuthenticated={isAuthenticated}
        isGuest={isGuest}
        onLogout={handleLogout}
        onLoginClick={apriAccesso}
        showSidebar={shouldShowSidebar && !orologioATuttoSchermo}
      >
        <Suspense fallback={null}>
          <OrologioScreen onCorsa={setOrologioInCorsa} />
        </Suspense>
        {cookieBanner}
        {challengeOverlays}
        {puzzleOverlay}
      </Layout>
    );
  }

  if (screen === 'estrai-apertura') {
    return (
      <Layout
        activeItem={getActiveNavItem()}
        onNavigate={handleNavigate}
        isAuthenticated={isAuthenticated}
        isGuest={isGuest}
        onLogout={handleLogout}
        onLoginClick={apriAccesso}
        showSidebar={shouldShowSidebar}
      >
        <Suspense fallback={null}>
          <EstraiAperturaScreen onOpenOpeningTables={openOpeningTables} />
        </Suspense>
        {cookieBanner}
        {challengeOverlays}
        {puzzleOverlay}
      </Layout>
    );
  }

  // Tournaments List screen
  if (screen === 'tournaments') {
    return (
      <Layout
        activeItem={getActiveNavItem()}
        onNavigate={handleNavigate}
        isAuthenticated={isAuthenticated}
        isGuest={isGuest}
        onLogout={handleLogout}
        onLoginClick={apriAccesso}
        showSidebar={shouldShowSidebar}
      >
        <Suspense fallback={<TournamentListSkeleton />}>
          <TournamentListPage
            onSelectTournament={(id) => {
              setSelectedTournamentId(id);
              setScreen('tournament-detail');
            }}
            isLoggedIn={isAuthenticated && !isGuest}
            onSignIn={apriAccesso}
            onSelectArena={(id) => { setSelectedArenaId(id); setScreen('arena-detail'); }}
            /* Il regolamento dei tornei ha una pagina sua, sotto l'indice
               delle regole: da qui la apre il rimando dentro il modale di
               creazione, sul capitolo che spiega da dove vengono i turni. */
            onOpenTournamentRules={openTournamentRules}
          />
        </Suspense>
        {cookieBanner}
        {challengeOverlays}
        {puzzleOverlay}
      </Layout>
    );
  }

  // Tournament Detail screen
  if (screen === 'tournament-detail' && selectedTournamentId) {
    return (
      <Layout
        activeItem={getActiveNavItem()}
        onNavigate={handleNavigate}
        isAuthenticated={isAuthenticated}
        isGuest={isGuest}
        onLogout={handleLogout}
        onLoginClick={apriAccesso}
        showSidebar={shouldShowSidebar}
      >
        <Suspense fallback={null}>
          <TournamentDetailPage
            tournamentId={selectedTournamentId}
            inviteToken={inviteTournamentId === selectedTournamentId ? inviteTournamentToken : null}
            /*
             * Il gettone d'invito e' stato RIFIUTATO: si butta qui, dove vive.
             *
             * Tenerlo sarebbe un vicolo cieco lento: la pagina del torneo da'
             * al gettone la precedenza sulla password, quindi a ogni rientro
             * dalla lista ne rimanderebbe uno morto, bruciando un tentativo per
             * volta — e dopo cinque `checkPrivateAccess` blocca la persona per
             * un quarto d'ora. Succede a chiunque abbia ancora il link vecchio
             * dopo che l'organizzatore ha rifatto le chiavi, cioe' il flusso
             * normale di quella funzione. Si toglie anche da `localStorage`,
             * che lo ripescherebbe per ventiquattr'ore.
             */
            onGettoneRifiutato={scartaGettoneInvito}
            onSpectatePlayer={handleSpectateFromTournament}
            /* Che cosa sono Buchholz e Sonneborn-Berger: la risposta sta nel
               regolamento dei tornei, e il rimando vive sotto la classifica. */
            onOpenTournamentRules={openTournamentRules}
            /*
             * L'OSPITE va alla conversione, non all'accesso.
             *
             * `isLoggedIn` qui e' `autenticato && !ospite`, quindi l'ospite vede
             * il bottone «accedi per partecipare» — e `apriAccesso` apre la
             * modale di LOGIN. Chi ha rating puzzle e streak da ospite si
             * creerebbe un conto nuovo da quella modale, lasciando i propri
             * progressi su un account che non usera' piu'. E' il difetto gia'
             * catalogato delle «26 schermate su 27 senza controllo isGuest»:
             * questa e' la 27ª, e si chiude qui invece di aggiungersi.
             */
            onSignIn={() => {
              if (isGuest) setShowUpgradeModal(true);
              else apriAccesso();
            }}
            onBack={() => {
              loadTournaments(); // Refresh list when going back
              setSelectedTournamentId(null);
              setScreen('tournaments');
            }}
            onJoin={() => {  setScreen('tournament-lobby'); }}
            isLoggedIn={isAuthenticated && !isGuest}
          />
        </Suspense>
        {cookieBanner}
        {challengeOverlays}
        {puzzleOverlay}
      </Layout>
    );
  }

  // Tournament Lobby screen
  if (screen === 'tournament-lobby') {
    return (
      <Layout
        activeItem={getActiveNavItem()}
        onNavigate={handleNavigate}
        isAuthenticated={isAuthenticated}
        isGuest={isGuest}
        onLogout={handleLogout}
        onLoginClick={apriAccesso}
        showSidebar={shouldShowSidebar}
      >
        <TournamentLobby
          onBack={() => {
            setScreen('tournament-detail');
          }}
        />
        {cookieBanner}
        {challengeOverlays}
        {puzzleOverlay}
      </Layout>
    );
  }

  // Tournament Game screen (no sidebar)
  if (screen === 'tournament-game') {
    return (
      <div className="game-fullscreen">
        <TournamentGame
          /*
           * Finita la partita si torna alla SCHEDA del torneo, non in lobby.
           *
           * Le due pagine mostrano le stesse tre schede con la stessa
           * classifica, ma l'occhio dello spettatore sta solo nella scheda — e
           * chi ha appena finito, mentre gli altri tabelloni vanno avanti, e'
           * esattamente la persona che vuole guardare la partita di qualcun
           * altro. Finendo in lobby non lo trovava, e premendo indietro
           * approdava sulla gemella: sembrava che il tasto non facesse niente
           * e che l'occhio comparisse per magia.
           *
           * Se intanto arriva l'accoppiamento del turno dopo, `match-found`
           * riporta comunque nella stanza: quella regola non cambia.
           */
          onGameEnd={() => {
            const idTorneo = useTournamentStore.getState().currentTournament?.id;
            if (idTorneo) setSelectedTournamentId(idTorneo);
            leaveGameTo('tournament-detail');
          }}
        />
        {leaveConfirmModal}
        {cookieBanner}
        {challengeOverlays}
        {puzzleOverlay}
      </div>
    );
  }

  // Fallback to home
  const fallbackDashboardActive = isAuthenticated && !isGuest && !!user;
  return (
    <Layout
      activeItem={getActiveNavItem()}
      onNavigate={handleNavigate}
      isAuthenticated={isAuthenticated}
      onLogout={handleLogout}
      onLoginClick={apriAccesso}
      showSidebar={!fallbackDashboardActive}
    >
      <AvvisoSenzaRete contesto="home" />
      <AvvisoDiServizio />
      {isGuest && <GuestBanner onUpgrade={() => {  setShowUpgradeModal(true); }} />}
      {isAuthenticated && !isGuest && user ? (
        <DashboardPage
          userId={user.id}
          username={user.username}
          avatar={user.avatar}
          onPlayAI={(variant, difficulty, timeControl, options) => handlePlayAI(variant as import('@draughts/shared').GameVariant, difficulty as 'very-easy' | 'easy' | 'medium' | 'hard' | 'expert' | 'master', timeControl as import('@draughts/shared').TimeControlPreset, options)}
          onPlayInPerson={handlePlayInPerson}
          onChallenge={handleChallengePlayer}
          onSitAtTable={sitAtLobbyTable}
          onCreateChallengeLink={handleCreateChallengeLink}
          onOpenOpeningTables={openOpeningTables}
          onSpectatePlayer={handleSpectatePlayer}
          onNavigateToProfile={() => { openProfile(user.username); }}
          onOpenOwnProfile={openProfile}
          onOpenLeaderboard={(v, d) => { openLeaderboard('mostGames', v, d); }}
          onNavigateToSettings={() => { openProfile(user.username, 'settings'); }}
          onLogout={handleLogout}
          onNavigateToArena={(id) => { setSelectedArenaId(id); setScreen('arena-detail'); }}
          onNavigateToTournament={(id) => { setSelectedTournamentId(id); setScreen('tournament-detail'); }}
          onNavigateToTournaments={() => { setScreen('tournaments'); }}
          onSolvePuzzle={openPuzzle}
          dailyPuzzlesOpen={showDailyPuzzles}
          onOpenDailyPuzzles={() => setShowDailyPuzzles(true)}
          onCloseDailyPuzzles={() => setShowDailyPuzzles(false)}
          onSolvePuzzleFromDailyList={openPuzzleFromDailyList}
          puzzleSheetOpen={puzzleOverlayId != null || puzzleOverlayDemo}
          onReplayGame={(id) => { setReplayGameId(id); setScreen('games-history'); }}
          onNavigateToFaq={openFaq}
        />
      ) : (
        <HomePage
          isAuthenticated={isAuthenticated}
          isGuest={isGuest}
          user={user}
          onPlayAI={handlePlayAI}
          onChallengePlayer={handleChallengePlayer}
          onCreateChallengeLink={handleCreateChallengeLink}
          onOpenOpeningTables={openOpeningTables}
          onSpectatePlayer={handleSpectatePlayer}
          onLoginRequired={apriAccesso}
          onCreateAccount={apriRegistrazione}
          onPlayAsGuest={handlePlayAsGuest}
          onNavigateToProfile={() => { if (user) { openProfile(user.username); } }}
          onNavigateToTournament={(id) => { setSelectedTournamentId(id); setScreen('tournament-detail'); }}
          onOpenDemoPuzzle={openDemoPuzzle}
          onOpenVariantRules={(v) => { setRulesVariant(v); setScreen('rules-variant'); }}
          onOpenGameRules={() => setScreen('rules-game')}
        />
      )}
      <LegalFooter
        onNavigateToTerms={() => setScreen('terms')}
        onNavigateToPrivacy={() => setScreen('privacy')}
        onNavigateToFaq={() => { openFaq(); }}
        onNavigateToRules={() => setScreen('rules-index')}
      />
      {cookieBanner}
        {challengeOverlays}
    </Layout>
  );
}

/** Global outgoing challenge overlay — shown while waiting for opponent response */
function GlobalOutgoingChallengeOverlay() {
  const outgoingChallenge = useOnlineStore((state) => state.outgoingChallenge);
  const setOutgoingChallenge = useOnlineStore((state) => state.setOutgoingChallenge);

  if (!outgoingChallenge) return null;

  const handleCancel = async () => {
    log.info('App', 'Outgoing challenge cancelled');
    try {
      await socketCancelChallenge(outgoingChallenge.id);
    } catch (e) { log.warn('App', 'Cancel outgoing challenge failed', { error: (e as Error)?.message }); }
    setOutgoingChallenge(null);
  };

  return (
    <OutgoingChallengeModal
      challenge={outgoingChallenge}
      onCancel={handleCancel}
    />
  );
}

/**
 * Global challenge-link overlay — the challenger's view of the link they are
 * offering. Closing it keeps the link alive (the friend can still accept, and
 * game:start arrives wherever the challenger is); only Cancel kills it.
 */
function GlobalChallengeLinkOverlay() {
  const linkChallenge = useOnlineStore((state) => state.linkChallenge);
  const setLinkChallenge = useOnlineStore((state) => state.setLinkChallenge);

  if (!linkChallenge) return null;

  const handleClose = () => {
    setLinkChallenge(null);
    clearStoredChallengeLinkId();
  };

  const handleCancel = async () => {
    log.info('App', 'Challenge link cancelled');
    try {
      await cancelLinkChallenge(linkChallenge.id);
    } catch (e) { log.warn('App', 'Cancel challenge link failed', { error: (e as Error)?.message }); }
    setLinkChallenge(null);
    clearStoredChallengeLinkId();
  };

  return (
    <ChallengeLinkModal
      link={linkChallenge}
      onCancel={handleCancel}
      onClose={handleClose}
    />
  );
}

export default App;
